import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, withdrawalDocUrlById } from "@/lib/api";
import { useSaveKey, withSaveKey } from "@/lib/saveOnce";
import { useAuth } from "@/context/AuthContext";
import { PageHelpPanel } from "@/components/PageHelpPanel";
import { PageHeader, Card, EmptyState, Btn, InfoTip, BALANCE_ENTRY_TIP } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DecisionDialog } from "@/components/DecisionDialog";
import { DeleteDialog } from "@/components/DeleteDialog";
import { DocumentsBox } from "@/components/DocumentsBox";
import { Progress } from "@/components/ui/progress";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { useDateErrors } from "@/lib/useDateErrors";
import { planChoices } from "@/lib/planChoices";
import { money, taxYears, taxYearsNonFuture, fmtDate, EXPENSE_GROUPS, stateName, localISO, nameOf, DISTRIBUTION_TYPE_LABEL, DISTRIBUTION_TYPE_ORDER, UNRECOGNISED_WORD } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { useParties } from "@/lib/parties";
import { psLabel, fundLast4 } from "@/lib/payments";
import { Bank, Plus, Warning, Trash, Stack, ArrowRight, CheckCircle, FilePdf, PencilSimple } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { withdrawalFields, RECORD_TITLES } from "@/lib/recordFields";
import { toast } from "sonner";
import { addableUsd, ceilingUsd, isFixedAtWhatItHolds, heldUsd, grantUsd, roomLeftOnWithdrawalUsd } from "@/lib/withdrawalHeadroom";
import { overWithdrawnSplit } from "@/lib/overWithdrawnSplit";
import { AWAITING_RATE_SENTENCE, headlineWords } from "@/lib/overWithdrawnSplit";
import { DecemberWithdrawalNote, OwnerRecipientNote } from "@/components/ComplianceNudges";
import { DistributionSummary } from "@/components/DistributionSummary";
import { DOCUMENT_ACCEPT } from "@/lib/uploads";
import { runUploads, attachmentFailureMessage } from "@/lib/uploadOutcome";
import { AttachmentButton, AttachmentPanel, AttachmentHelp, useAttachmentPanel } from "@/components/AttachmentView";
import { isUSD } from "@/lib/currencies";
import { NEEDS_A_RATE_BADGE, NEEDS_A_RATE_HOVER, AWAITING_A_RATE_BADGE, AWAITING_A_RATE_HOVER } from "@/lib/fxPanel";
import { planFill, NAV_STATE_KEY, FIGURE_CHANGED_NOTE } from "@/lib/withdrawThisAmount";
import { untiedOf, EDIT_WITHDRAWAL_KEY } from "@/lib/untiedWithdrawals";
import { UntiedWithdrawalReminder } from "@/components/UntiedWithdrawalReminder";
import { formPlanSentence } from "@/lib/tieFirst";
import { currentTaxYear, yearToOpenOn } from "@/lib/startingTaxYear";
import { PastAllowanceTick, useAllowanceRefusal, tickCovers, ackToSend } from "@/components/PastAllowanceTick";
import { allowanceRoomTracker, roomStillLeft } from "@/lib/withdrawalHeadroom";
import { PAY_REST_OUTSIDE_LINK } from "@/lib/aboveAllowance";

// THE KINDS OF DISTRIBUTION, AND THEIR ORDER ON THE FORM. (ZEN-635.) The words
// and the order both come from lib/format.js, and the order is pinned against
// the words by backend/tests/test_screen_word_parity.py.
//
// THE FIRST DRAFT OF THIS LINE SPELLED THE FOUR KEYS OUT HERE and claimed in a
// comment that the build would catch a fifth. It would not: parity compares the
// server's list to the WORDS table, and this list was a fifth hand copy nobody
// read. Proved by adding a kind — every check stayed green and the form simply
// had no button for it, so nobody could record that kind of distribution.
// Exactly the fault this ticket is about, written into the fix for it.
// (Found by reviewing this change, 2026-09-15.)
// APPROVED by Maryse 17 Oct 2026 (ZEN-681), exact words. Expenses the server
// left out of the list because they have no US-dollar amount (unpriced_count).
// Why a withdrawal row's figure is below the expense's cost. Wording approved
// by Maryse, 2 Nov 2026 (ZEN-334). Null when nothing is set aside.
export const setAsideNote = (usd) =>
  usd > 0.005 ? `${money(usd, "USD")} set aside for the tax credit` : null;

export const notShownNoDollarAmount = (n) =>
  `${n} expense${n === 1 ? "" : "s"} not shown — no US-dollar amount yet. Add the rate on the expense to tie it to this withdrawal.`;

// ZEN-834 — APPROVED by Maryse 31 Oct 2026 in the ZEN-834 session, her words
// "approve as written". The one copy of this note. Shown in
// place of the tick box on an expense whose exchange rate is still an
// estimate: it can be drawn ahead for, but tied only once its rate is final.
export const RATE_WAIT_NOTE = "tie once its rate is final";

// Which rows wait only for their rate. The SERVER decides (the code is the
// shared rule's), never this screen. One reading for both pickers.
// Only rows with money to show: both pickers share this one filter, so a
// "≈ $0" waiting row cannot appear in one and not the other (Codex, round 2).
// It reads the figure the row DISPLAYS. Not addableUsd: that is zero for every
// row without a tick box, which a waiting row never has, so it would hide them all.
export const rateWaitRows = (items) => (items || []).filter(
  (it) => it.not_selectable_reason === "fx_estimate"
    && !((it.allocated_to_editing || 0) > 0.005)
    && (Number(it.withdraw_usd) || 0) > 0.005);

// Listed, not hidden: a family looking for next month's rent should see it is
// there and why it cannot be ticked yet. No tick box, on purpose.
export function RateWaitRows({ items, testid }) {
  if (!items.length) return null;
  return (
    <div className="mt-2 border-t border-[#E9E9EB] pt-2" data-testid={testid}>
      {items.map((it) => (
        <div key={it.id} className="flex flex-wrap items-center justify-between gap-2 rounded px-1.5 py-1 text-xs text-[#6b7280]" data-testid={`${testid}-row-${it.id}`}>
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="tnum">{fmtDate(it.transaction_date)}</span>
            <span className="min-w-0 break-words font-semibold">{it.payee}</span>
            <span className="rounded-full bg-[#034AA2]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#034AA2]" data-testid={`${testid}-note-${it.id}`}>{RATE_WAIT_NOTE}</span>
          </span>
          <span className="tnum ml-auto shrink-0">≈ {money(it.withdraw_usd, "USD")}</span>
        </div>
      ))}
    </div>
  );
}

const DIST_TYPES = DISTRIBUTION_TYPE_ORDER
  .map((key) => ({ key, label: DISTRIBUTION_TYPE_LABEL[key] }));
const DIST_BADGE_STYLE = {
  qualified_education: "bg-[#034AA2] text-white",
  student_loan_repayment: "bg-[#0F766E] text-white",
  roth_rollover: "bg-[#0369A1] text-white",
  non_qualified: "bg-[#F01919]/10 text-[#F01919]",
};

// What the reader is checking the document AGAINST, said in one line, so the
// panel and the button that opens it both name the distribution they belong to.
export function withdrawalLabel(w) {
  // The DATE THE MONEY LEFT THE PLAN where it is known, and the requested date
  // otherwise — the same pair, in the same order of preference, that the rest
  // of this screen and the tax-year rules use. There is no plain "date" field
  // on a distribution, and naming one produced a label that read
  // "$4,820.00 distribution on" with nothing after it.
  const when = w.date_received || w.date_requested;
  return when ? `${money(w.amount, "USD")} distribution on ${fmtDate(when)}` : `${money(w.amount, "USD")} distribution`;
}

function withdrawalAttachment(w, d) {
  return {
    key: `${w.id}:${d.id}`,
    label: d.filename || "Document",
    fileUrl: withdrawalDocUrlById(w.id, d.id),
  };
}

export default function Withdrawals() {
  const { selectedStudentId } = useAuth();
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(false);
  // THE EXPENSES BEHIND THE FIGURE THAT WAS CLICKED, handed over by the
  // Expenses page's "Withdraw this amount" button — the list itself, not a
  // description of it the form would have to work out again. (ZEN-582)
  const [fromFigure, setFromFigure] = useState(null);
  const [editingWithdrawal, setEditingWithdrawal] = useState(null);
  // The distribution behind the eye — read only, from the list already on
  // screen. NO lookup is loaded for it: naming a payer who received the money
  // would take a request this screen does not otherwise make, and the panel
  // may not add one. (ZEN-702; adversarial review, round 1)
  const [viewingWithdrawal, setViewingWithdrawal] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Opens on the current tax year, or the most recent one with a distribution
  // in it, like the Expenses page; then whatever is picked. (ZEN-762)
  const [pickedTaxYear, setTaxYearFilter] = useState(null);
  // Looking at a plan statement must not cost you the distribution beside it —
  // the amount, the date and how much of it is justified are exactly what the
  // statement is being checked against. (ZEN-351)
  const docPanel = useAttachmentPanel();
  const sid = selectedStudentId;

  // OPEN ONE WITHDRAWAL'S EDIT FORM — from "Tie it to expense(s)" on the
  // Expenses page or on this form. The form is closed first so it resets
  // as for any other edit, then opened once the list holds the record. (ZEN-785)
  const [pendingEditId, setPendingEditId] = useState(null);
  useEffect(() => {
    const id = location.state?.[EDIT_WITHDRAWAL_KEY];
    if (id) {
      setPendingEditId(id);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line

  useEffect(() => {
    const handed = location.state?.[NAV_STATE_KEY];
    if (handed) {
      // Stamped so the form asks the server afresh for THIS click and can
      // never fill itself in from an answer it already had. (ZEN-582)
      setFromFigure({ ...handed, askedAt: `${Date.now()}-${Math.random()}` }); setDlg(true);
      // Spent once: a reload or a step back must not reopen a form filled from
      // a figure the page may no longer show.
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line

  const { data: withdrawals = [], isLoading } = useQuery({ queryKey: ["withdrawals", sid], queryFn: async () => (await api.get(`/withdrawals?student_id=${sid}`)).data, enabled: !!sid });
  useEffect(() => {
    if (!pendingEditId || dlg || isLoading) return;
    const w = withdrawals.find((x) => x.id === pendingEditId);
    setPendingEditId(null);
    if (w) { setEditingWithdrawal(w); setDlg(true); }
  }, [pendingEditId, dlg, isLoading, withdrawals]);
  // THE PAGE ASKS AS OF TODAY, and it has to SAY so. The "you can draw ahead"
  // figure is only computed when the server knows which day the money would come
  // out — that is what decides whether an expense still ahead of its date falls in
  // the same calendar year. Left unsaid, the server had no date to compare
  // against, the figure came back zero every time, and the panel this ticket
  // adds could never appear on this page. (Diff review, round 2.)
  const todayISO = new Date().toISOString().slice(0, 10);
  const { data: eligSummary } = useQuery({ queryKey: ["wd-eligible-preview", sid, todayISO], queryFn: async () => (await api.get(`/withdrawals/eligible-preview?student_id=${sid}&distribution_date=${todayISO}`)).data, enabled: !!sid });
  const currentYear = new Date().getFullYear();
  const { data: dashData } = useQuery({ queryKey: ["dash-student", sid, currentYear], queryFn: async () => (await api.get(`/dashboard/student/${sid}?tax_year=${currentYear}`)).data, enabled: !!sid });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const beneficiaryName = students.find((s) => s.id === sid)?.name || "";

  // Saving a distribution changes the derived 529 balance, so the account
  // cards ("bal"/"cps") and projections must refetch too — without this they
  // kept showing the pre-save figures for up to the 60s stale window.
  const refresh = () => { qc.invalidateQueries({ queryKey: ["withdrawals"] }); qc.invalidateQueries({ queryKey: ["expenses"] }); qc.invalidateQueries({ queryKey: ["wd-eligible-preview"] }); qc.invalidateQueries({ queryKey: ["dash-student"] }); qc.invalidateQueries({ queryKey: ["bal"] }); qc.invalidateQueries({ queryKey: ["cps"] }); qc.invalidateQueries({ queryKey: ["projection-inputs"] }); };

  // Derive distinct tax years from loaded withdrawals for the filter
  const availableYears = React.useMemo(() => {
    // The current year is always offered, so a family whose distributions are
    // all from another year still gets the buttons to reach them. (ZEN-762)
    // So is a year picked by hand, even after its last distribution is deleted:
    // the year on screen is always one of the buttons.
    const yrs = [...new Set([Number(currentTaxYear()), Number(pickedTaxYear),
      ...withdrawals.map((w) => Number(w.eligibility_tax_year))].filter(Boolean))].sort((a, b) => b - a);
    return yrs;
  }, [withdrawals, pickedTaxYear]);

  const taxYearFilter = pickedTaxYear
    ?? (yearToOpenOn(withdrawals.map((w) => w.eligibility_tax_year)) || "all");
  const filteredWithdrawals = taxYearFilter === "all" ? withdrawals : withdrawals.filter((w) => String(w.eligibility_tax_year) === String(taxYearFilter));

  if (!sid) return <div><PageHeader title="529 Distributions" /><PageHelpPanel pageKey="withdrawals" /><EmptyState icon={Bank} title="Select a beneficiary" subtitle="Choose a beneficiary from the top bar to manage their 529 distributions." /></div>;

  return (
    <div>
      {/* Same explainer as Gifts, same reason: cards at every width, and no
          gap line because a distribution's documents live inline on the
          record itself. (ZEN-455) */}
      <PageHeader title={<>529 Distributions<span className="ml-1.5 align-middle"><AttachmentHelp testid="wd-paperclip-help" /></span></>}
        subtitle={<>Distributions don&apos;t have to land in the beneficiary&apos;s account, but it&apos;s usually the better choice. See <Link to="/help?article=withdrawal-recipient" className="font-semibold text-[#034AA2] hover:underline">the Help Center</Link> for why.</>}
        actions={<Btn onClick={() => setDlg(true)} data-testid="add-withdrawal-btn"><Plus size={16} weight="bold" /> Add 529 distribution</Btn>} />
      <PageHelpPanel pageKey="withdrawals" />

      <DistributionSummary statutoryCaps={dashData?.statutory_caps} groupCaps={dashData?.group_caps} aqee={dashData?.aqee} beneficiaryName={beneficiaryName} taxYear={currentYear} />

      {/* Tax year filter */}
      {availableYears.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="wd-taxyear-filter">
          <span className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Tax year:</span>
          <button
            onClick={() => setTaxYearFilter("all")}
            data-testid="wd-taxyear-all"
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${taxYearFilter === "all" ? "border-[#034AA2] bg-[#034AA2] text-white" : "border-[#C2C9D1] bg-white text-[#374151] hover:border-[#034AA2] hover:text-[#034AA2]"}`}
          >All years</button>
          {availableYears.map((yr) => (
            <button
              key={yr}
              onClick={() => setTaxYearFilter(String(yr))}
              data-testid={`wd-taxyear-${yr}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${String(taxYearFilter) === String(yr) ? "border-[#034AA2] bg-[#034AA2] text-white" : "border-[#C2C9D1] bg-white text-[#374151] hover:border-[#034AA2] hover:text-[#034AA2]"}`}
            >{yr}</button>
          ))}
        </div>
      )}

      <BreakdownCard eligSummary={eligSummary} year={taxYearFilter} onSeeYear={(y) => setTaxYearFilter(String(y))} />

      {isLoading ? <div className="h-40 animate-pulse rounded-xl bg-white" /> : filteredWithdrawals.length === 0 ? (
        <EmptyState icon={Bank} title={taxYearFilter === "all" ? "No 529 distributions yet" : `No 529 distributions for ${taxYearFilter}`} subtitle={taxYearFilter === "all" ? "Record a reimbursement or lump-sum distribution." : "Try selecting a different tax year or 'All years'."} />
      ) : (
        <div className="space-y-3">
          {filteredWithdrawals.map((w) => {
            const pct = w.amount ? Math.min(100, Math.round((w.allocated_total / w.amount) * 100)) : 0;
            return (
              <Card key={w.id} className={w.is_exception ? "border-[#F01919]" : ""} data-testid={`withdrawal-${w.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    {/* On a narrow screen the badges and the amount STACK, and no
                        badge breaks inside its own pill. (ZEN-669) */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#034AA2] px-2.5 py-0.5 text-xs font-bold text-white">
                        {w.mode === "lump_sum" ? <Stack size={12} weight="bold" /> : <ArrowRight size={12} weight="bold" />}{w.mode === "lump_sum" ? "Lump sum" : "Reimbursement"}
                      </span>
                      {w.distribution_type && w.distribution_type !== "qualified_education" && (
                        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold ${DIST_BADGE_STYLE[w.distribution_type] || "bg-[#E9E9EB] text-[#374151]"}`}>
                          {/* was `DIST_LABEL[...] || w.distribution_type` — a kind
                              this list did not know was shown to a family as the
                              stored word itself. (ZEN-635) */}
                          {nameOf(DISTRIBUTION_TYPE_LABEL, w.distribution_type,
                                  UNRECOGNISED_WORD)}
                        </span>
                      )}
                      {w.distribution_type === "roth_rollover" && w.trustee_to_trustee === false && (
                        /* ONE PHRASE FOR ONE TAX CONSEQUENCE. (ZEN-650.) This
                           read "non-qualified (not trustee-to-trustee)" while
                           the badge beside it said "Not 529-qualified" (the
                           wording of the day; ZEN-664 later replaced it) — the
                           same consequence, two wordings, touching each other
                           on screen, and a family had no way to know they meant
                           the same thing. Maryse's ruling, 2026-09-16: use the
                           approved phrase and drop the parenthetical, which
                           explained the mechanism rather than the outcome.
                           Taken from the shared table rather than written out
                           again, so the two badges cannot drift apart a second
                           time. They can never both show it: this one appears
                           only on a Roth rollover, whose own badge reads
                           "Roth IRA rollover". */
                        <span className="inline-flex items-center whitespace-nowrap rounded-full bg-[#F01919]/10 px-2 py-0.5 text-[10px] font-semibold text-[#F01919]">{DISTRIBUTION_TYPE_LABEL.non_qualified}</span>
                      )}
                      <span className="inline-flex items-center gap-2 whitespace-nowrap">
                        <span className="text-lg font-extrabold tnum">{money(w.amount, "USD")}</span>
                        <span className="text-xs text-[#6b7280]">tax year {w.eligibility_tax_year}</span>
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-[#6b7280]">
                      Requested {fmtDate(w.date_requested)}{w.date_received ? ` · received ${fmtDate(w.date_received)}` : <span className="ml-1 rounded bg-[#D97706]/10 px-1.5 py-0.5 text-xs font-semibold text-[#D97706]">date received pending</span>} · to {w.intended_recipient}
                    </div>
                    {w.balance_prior != null && <div className="text-xs text-[#6b7280]">Balance {money(w.balance_prior, "USD")} → {money(w.balance_after, "USD")}</div>}
                    {/* Tie-status clue — visible on collapsed card; lump_sum already shows a progress bar below */}
                    {w.mode === "reimbursement" && (
                      w.remaining < -0.005
                        ? <div className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-[#F01919]" data-testid={`wd-tie-status-${w.id}`}>
                            Over-allocated by {money(Math.abs(w.remaining), "USD")} — the expenses linked to this 529 distribution are worth more than it distributed.
                            <OverAllocTip />
                          </div>
                        : w.allocated_total >= w.amount - 0.005
                          ? (w.different_tax_year_total > 0.005
                              ? <div className="mt-1.5 text-xs font-semibold text-[#F01919]" data-testid={`wd-tie-status-${w.id}`}>Tied total {money(w.allocated_total, "USD")} / {money(w.amount, "USD")} · but {money(w.different_tax_year_total, "USD")} of it is matched to a cost from a different tax year</div>
                              : <div className="mt-1.5 text-xs font-semibold text-[#077A39]" data-testid={`wd-tie-status-${w.id}`}>Tied total {money(w.allocated_total, "USD")} / {money(w.amount, "USD")} · fully justified ✓</div>)
                          : w.allocated_total > 0.005
                            ? <div className="mt-1.5 text-xs font-semibold text-[#B45309]" data-testid={`wd-tie-status-${w.id}`}>Tied {money(w.allocated_total, "USD")} of {money(w.amount, "USD")} · {money(w.remaining || w.amount - w.allocated_total, "USD")} still to justify</div>
                            : <div className="mt-1.5 text-xs font-semibold text-[#F01919]" data-testid={`wd-tie-status-${w.id}`}>No expenses tied yet — tie expenses to complete this 529 distribution</div>
                    )}
                  </div>
                </div>

                {w.is_exception && (
                  <div className="mt-3 flex items-start gap-2 rounded-md bg-[#F01919]/10 px-3 py-2 text-sm text-[#F01919]" data-testid={`exception-${w.id}`}>
                    <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />
                    <div><b>Exception:</b> funds actually received by the <b>Owner</b>, not the beneficiary. The 1099-Q will be issued in the owner's name/SSN. Reconciliation must account for funds sitting with the owner. {w.actual_recipient_note}</div>
                  </div>
                )}

                {w.mode === "lump_sum" && (
                  <div className="mt-3" data-testid={`alloc-${w.id}`}>
                    <div className="mb-1 flex items-center justify-between text-xs font-semibold">
                      <span>Justified by tied expenses</span>
                      <span className="tnum">{money(w.allocated_total, "USD")} / {money(w.amount, "USD")} · <span className={w.remaining < -0.005 ? "text-[#F01919]" : w.remaining > 0.009 ? "text-[#F01919]" : "text-[#077A39]"}>
                        {w.remaining < -0.005 ? `over-allocated by ${money(Math.abs(w.remaining), "USD")}` : w.remaining > 0.009 ? `${money(w.remaining, "USD")} still to justify` : (w.different_tax_year_total > 0.005 ? "see the note below" : "fully justified ✓")}
                      </span></span>
                    </div>
                    <Progress value={pct} className="h-2.5" />
                    <AllocPicker withdrawal={w} onDone={refresh} />
                  </div>
                )}

                {/* THE FIGURE ABOVE COUNTS MONEY THAT LEFT THE PLAN, INCLUDING
                    MONEY DRAWN AGAINST AN EXPENSE NOBODY HAS PAID YET. That is
                    right — hiding it would invite the account owner to draw the
                    same money twice — but "fully justified" is a claim about a
                    tax position, and it must not be made about an expense in a
                    DIFFERENT CALENDAR YEAR, which is the one pairing this app
                    refuses outright. Two lines, deliberately different in weight:
                    paying ahead is ordinary, the other is a problem.
                    (Diff review, round 8.)

                    OUTSIDE THE LUMP-SUM BLOCK, because a distribution recorded
                    the other way round — as a reimbursement — can hold exactly
                    the same wrong-year match and was showing no warning at all.
                    (Round 12.) */}
                {w.different_tax_year_total > 0.005 && (
                  <div className="mt-1.5 text-xs font-semibold text-[#F01919]" data-testid={`wd-wrong-year-${w.id}`}>
                    {money(w.different_tax_year_total, "USD")} of this is matched to a cost that counts toward a different tax year, so it is not justified here — open the cost and move its tax year to this withdrawal&rsquo;s, or remove the match.
                  </div>
                )}
                {/* THE SERVER WORKS THIS OUT. Subtracting the wrong-year figure from
                    the unpaid one here was arithmetic on two sets that only partly
                    overlap — a wrong-year match on an expense already PAID sits in the
                    second and not the first, and ate into an unrelated pre-funded
                    figure. (Round 14.) */}
                {w.prefunded_same_year_total > 0.005 && (
                  <div className="mt-1.5 text-xs text-[#034AA2]" data-testid={`wd-not-paid-yet-${w.id}`}>
                    Includes {money(w.prefunded_same_year_total, "USD")} drawn ahead for costs that are not due yet.
                  </div>
                )}
                {w.notes && <div className="mt-2 text-xs text-[#6b7280]">{w.notes}</div>}
                {/* THE PAPERCLIP OPENS THE DOCUMENT, IN PLACE. These used to be
                    links that threw the reader into a new browser tab, which is
                    the same loss of context as a window over the list: the
                    distribution's figures are gone the moment you look at the
                    statement meant to prove them. The documents are already
                    listed by name here, so each name is its own button and
                    opens beneath the card. (ZEN-351) */}
                {(w.documents?.length > 0) && (
                  <>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`wd-doc-list-${w.id}`}>
                      {w.documents.map((d) => {
                        const k = `${w.id}:${d.id}`;
                        return (
                          <AttachmentButton
                            key={d.id}
                            ref={docPanel.buttonRef(k)}
                            variant="chip"
                            attachments={[withdrawalAttachment(w, d)]}
                            recordLabel={withdrawalLabel(w)}
                            expanded={docPanel.isOpen(k)}
                            panelId={docPanel.panelId(k)}
                            onToggle={() => docPanel.toggle(k)}
                            testid={`wd-doc-link-${w.id}-${d.id}`}
                          />
                        );
                      })}
                    </div>
                    {w.documents.map((d) => {
                      const k = `${w.id}:${d.id}`;
                      return docPanel.isOpen(k) ? (
                        <AttachmentPanel
                          key={k}
                          id={docPanel.panelId(k)}
                          attachments={[withdrawalAttachment(w, d)]}
                          recordLabel={withdrawalLabel(w)}
                          onClose={() => docPanel.close(k)}
                          testid={`wd-doc-panel-${w.id}-${d.id}`}
                        />
                      ) : null;
                    })}
                  </>
                )}
                <WithdrawalDetails w={w} onDeleted={refresh} onEdit={(w) => { setEditingWithdrawal(w); setDlg(true); }} onView={(w) => setViewingWithdrawal(w)} />
              </Card>
            );
          })}
        </div>
      )}

      <WithdrawalDialog open={dlg} onClose={() => { setDlg(false); setFromFigure(null); setEditingWithdrawal(null); }} onSaved={refresh} studentId={sid} fromFigure={fromFigure} editingWithdrawal={editingWithdrawal}
        untied={untiedOf(withdrawals)}
        onTieUntied={(w) => { setDlg(false); setFromFigure(null); setEditingWithdrawal(null); setPendingEditId(w.id); }} />
      <RecordView
        open={!!viewingWithdrawal}
        onClose={() => setViewingWithdrawal(null)}
        title={RECORD_TITLES.withdrawal}
        subtitle={viewingWithdrawal ? withdrawalLabel(viewingWithdrawal) : ""}
        fields={viewingWithdrawal ? withdrawalFields(viewingWithdrawal) : []}
        testid="withdrawal-view"
      />
    </div>
  );
}

function WithdrawalDetails({ w, onDeleted, onEdit, onView }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["wd-tied", w.id],
    queryFn: async () => (await api.get(`/withdrawals/${w.id}/tied`)).data,
    enabled: open,
  });
  const tied = data?.items || [];
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/withdrawals/${w.id}`);
      toast.success("529 distribution deleted — tied expenses returned to Awaiting");
      setConfirm(false); setConfirmText(""); onDeleted();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border-t border-[#E9E9EB] pt-2">
      <button onClick={() => setOpen((o) => !o)} data-testid={`wd-details-btn-${w.id}`} className="text-xs font-semibold text-[#034AA2] hover:underline">
        {open ? "Hide details" : "View details"}
      </button>
      {open && (
        <div className="mt-2 rounded-md bg-[#F7F8FA] p-2.5" data-testid={`wd-details-${w.id}`}>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Tied expenses</div>
          {isLoading ? <div className="text-xs text-[#6b7280]">Loading…</div>
            : tied.length === 0 ? <div className="text-xs text-[#6b7280]">No expenses tied to this 529 distribution yet.</div>
              : <div className="divide-y divide-[#E9E9EB]">
                  <div className="flex items-center justify-between py-1 text-[10px] font-bold uppercase tracking-wide text-[#9aa1a9]">
                    <span>Expense</span>
                    <span>USD</span>
                  </div>
                  {tied.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-2 py-1.5 text-xs" data-testid={`wd-tied-row-${e.id}`}>
                      <div className="min-w-0">
                        <div className="truncate font-semibold">{e.payee}</div>
                        <div className="text-[#6b7280]">{fmtDate(e.transaction_date)} · {e.category}{!isUSD(e.original_currency) ? ` · ${money(e.original_amount, e.original_currency)}` : ""}</div>
                        {setAsideNote(e.set_aside_usd) && <div className="text-[#6b7280]" data-testid={`wd-tied-setaside-${e.id}`}>{setAsideNote(e.set_aside_usd)}</div>}
                      </div>
                      <div className="whitespace-nowrap text-right">
                        <span className="font-bold tnum">{money(e.amount_usd, "USD")}</span>
                        {e.partial && <span className="ml-1 rounded-full bg-[#7D3306]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#7D3306]">of {money(e.full_usd, "USD")}</span>}
                        {/* No US-dollar amount: no "of $X", the approved badge instead (ZEN-682). */}
                        {e.unpriced && <span className="ml-1 rounded-full bg-[#D97706]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#92400E]" title={e.is_fx_estimate ? AWAITING_A_RATE_HOVER : NEEDS_A_RATE_HOVER} data-testid={`wd-tied-unpriced-${e.id}`}>{e.is_fx_estimate ? AWAITING_A_RATE_BADGE : NEEDS_A_RATE_BADGE}</span>}
                        {/* The server has returned this on every row since ZEN-158
                            and nothing rendered it, so a distribution held against
                            an unpaid expense looked identical to one held against a
                            paid one. (Diff review, round 8.) */}
                        {e.different_tax_year
                          ? <span className="ml-1 rounded-full bg-[#F01919]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#F01919]" data-testid={`wd-tied-wrong-year-${e.id}`}>different tax year</span>
                          : e.not_yet_paid
                            ? <span className="ml-1 rounded-full bg-[#034AA2]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#034AA2]" data-testid={`wd-tied-not-paid-${e.id}`}>not due yet</span>
                            : null}
                      </div>
                    </div>
                  ))}
                </div>}
          <div className="mt-2 flex items-center justify-between border-t border-[#E9E9EB] pt-2">
            <div className="flex items-center gap-3">
              {/* The eye, immediately before the pencil. (ZEN-702) */}
              <ViewButton onClick={() => onView(w)} testid={`wd-view-btn-${w.id}`} size={12}>View 529 distribution</ViewButton>
              <button onClick={() => onEdit(w)} data-testid={`wd-edit-btn-${w.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#034AA2] hover:underline">
                <PencilSimple size={12} weight="bold" /> Edit 529 distribution
              </button>
              <button onClick={() => setConfirm(true)} data-testid={`wd-delete-link-${w.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F01919] hover:underline">
                <Trash size={12} weight="bold" /> Delete 529 distribution
              </button>
            </div>
            <span className="text-[11px] font-semibold text-[#6b7280]">Tied total {money(data?.allocated_total || 0, "USD")} / {money(w.amount, "USD")}</span>
          </div>
        </div>
      )}
      <Dialog open={confirm} onOpenChange={(o) => { if (!o) { setConfirm(false); setConfirmText(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete this 529 distribution?</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm text-[#374151]">
            <p>This <b>permanently</b> deletes the <b>{money(w.amount, "USD")}</b> {w.mode === "lump_sum" ? "lump-sum" : "reimbursement"} from {fmtDate(w.date_requested)}.</p>
            <p>Also, its <b>{tied.length || "tied"}</b> expense{tied.length === 1 ? "" : "s"} will return to <b>Awaiting</b>.</p>
            <p className="font-bold">This can&apos;t be undone.</p>
            <label className="block text-xs font-semibold text-[#6b7280]">
              To delete, type <span className="font-bold text-[#F01919]">DELETE</span>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid={`wd-delete-confirm-input-${w.id}`}
                className="mt-1 block h-9 w-full rounded-md border border-[#C2C9D1] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#F01919]/30" />
            </label>
          </div>
          <DialogFooter className="mt-2 gap-2">
            <button onClick={() => { setConfirm(false); setConfirmText(""); }}
              className="flex-1 rounded-md bg-[#077A39] px-4 py-2 text-sm font-bold text-white hover:bg-[#065f2c]"
              data-testid={`wd-delete-cancel-${w.id}`}>Cancel</button>
            <button onClick={doDelete} disabled={confirmText.trim().toUpperCase() !== "DELETE" || busy} data-testid={`wd-delete-confirm-btn-${w.id}`}
              className="flex-1 rounded-md bg-[#F01919] px-4 py-2 text-sm font-bold text-white hover:bg-[#c81414] disabled:opacity-40">
              {busy ? "Deleting…" : "Delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// THE FACTS A CROSS-YEAR NOTE WAS WRITTEN AGAINST, as one comparable string.
//
// A half-written note lives in a screen's own state, and the "observed" values
// sent with it are read from the LATEST preview at save time. So a note typed
// against one set of facts could be submitted as confirmation of another — the
// beneficiary edits the cost, or the account owner changes the withdrawal's
// date, and the note silently re-points. The panel closed this by rebuilding the
// card when its fingerprint moves; these two editors keep their notes in a map
// and needed the same idea spelled out: a draft belongs to the facts it was
// written against, and stops existing when they change. (Diff review, round 16.)
function noteFacts(it, distributionDate) {
  if (!it) return "";
  return [it.transaction_date || "", it.payee || "",
          it.base_usd == null ? "" : it.base_usd, distributionDate || ""].join("|");
}

// Exported so the wiring can be tested: the rule living in withdrawalHeadroom.js
// is worth nothing if this panel stops asking it, and nothing else can see that.
// (ZEN-380, adversarial review.)
export function AllocPicker({ withdrawal, onDone }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  // WHAT EACH TICKED COST WILL ACTUALLY MOVE, decided as it is ticked. (ZEN-380)
  //
  // A cost's own room and the withdrawal's remaining money are two different
  // ceilings, and this panel only ever knew the first. Ticking a US$900 cost
  // against a US$500 withdrawal sent US$900 and recorded a withdrawal as
  // justifying more money than came out of it — the figure a family reads to
  // decide whether they have over-drawn.
  //
  // The withdrawal dialog has always done this subtraction while building a
  // withdrawal; this is the same arithmetic, from the shared rule rather than a
  // second copy of it. Tick costs until the withdrawal is fully allocated and
  // the last one takes only what is left, its balance staying on the expense as
  // still to be reimbursed.
  const [grants, setGrants] = useState({});
  // ...AND IT IS LET GO WHENEVER THE WITHDRAWAL ITSELF MOVES. The same
  // double-count arrives with no save of ours: the withdrawal dialog, or a tie
  // on another card, refreshes this row while the panel sits open. What was
  // ticked was measured against figures that no longer exist. (ZEN-380)
  //
  // ...AND WHENEVER A TICKED COST ITSELF MOVES. The withdrawal's own figures are
  // only half of it: another panel can reprice or fully cover the very cost that
  // is ticked here, and the preview those two share is refreshed together. The
  // room on that row then shrinks or vanishes while the button goes on promising
  // the old amount, and the save is capped or refused. Only the SELECTED rows
  // are watched, so an unrelated refresh does not throw away a pick in progress.
  // (Adversarial review, round 1, P2.)
  const figures = `${withdrawal.amount}|${withdrawal.allocated_total}`;
  const lastFigures = useRef(figures);
  const factAtTick = useRef({});
  const factOfRow = (it) => (it
    ? `${it.withdraw_usd}:${it.allocated_to_editing}:${it.selectable}:${it.may_increase}`
    : "gone");
  useEffect(() => {
    if (lastFigures.current === figures) return;
    lastFigures.current = figures;
    setSel([]); setXSel([]); setXNotes({}); setGrants({});
  }, [figures]);
  // THIS DISTRIBUTION'S OWN SHARE HAS TO COME BACK IN THE ANSWER. (ZEN-170)
  //
  // Without editing_withdrawal_id the server nets out EVERY match on an expense,
  // including this distribution's, so withdraw_usd is "how much more could be
  // tied" — an increment. The amount is now read as a TOTAL, and sending an
  // increment as a total is destructive: a US$1,500 expense already backed by
  // US$1,000 of this distribution reported US$500, and saving that would have
  // replaced the US$1,000 with US$500 under a success message. With the id
  // passed, withdraw_usd excludes this distribution's share, so it IS the total
  // this distribution may hold, and allocated_to_editing says how much of that
  // it already holds. Same question the edit dialog asks, for the same reason.
  const { data: preview } = useQuery({
    queryKey: ["wd-eligible-preview", withdrawal.student_id, withdrawal.date_requested, withdrawal.date_received, withdrawal.id],
    queryFn: async () => (await api.get(`/withdrawals/eligible-preview?student_id=${withdrawal.student_id}&as_of_date=${withdrawal.date_requested}&distribution_date=${withdrawal.date_received || withdrawal.date_requested}&editing_withdrawal_id=${withdrawal.id}`)).data,
    enabled: open,
  });
  // WHAT THIS BUTTON ADDS is the total it may hold minus what it already holds.
  // The figure on screen keeps meaning exactly what it meant before — new money
  // this click moves — while the figure SENT is the total. A row with nothing
  // left to add is dropped, which is the set this list showed before the id was
  // passed; without this it would start listing expenses it is already full on.
  // THE SHARED RULE, not a fourth copy of it. This was the arithmetic written
  // out here, with the two permission flags checked separately a few lines down;
  // the same question was answered three more times elsewhere in the app and one
  // of the answers disagreed. One definition now, in
  // frontend/src/lib/withdrawalHeadroom.js. (ZEN-190/ZEN-198)
  // BY WHATEVER ROUTE IS OPEN — plainly for an ordinary expense, or with a recorded
  // reason for one paid in another calendar year. This same function feeds both
  // lists below, and an earlier draft that read only the plain route emptied the
  // cross-year list entirely: a whole feature gone, with nothing to show it.
  const newMoney = (it) => addableUsd(it);
  // THE MONEY STILL UNSPENT ON THIS WITHDRAWAL, less whatever is already ticked.
  // Read from the withdrawal the card is showing, so the panel and the figure
  // above it can never disagree. (ZEN-380)
  const grantedSoFar = Object.values(grants).reduce((s2, v) => s2 + (Number(v) || 0), 0);
  const roomLeft = Math.round((roomLeftOnWithdrawalUsd(withdrawal) - grantedSoFar) * 100) / 100;
  // TICKING IS WHERE THE ARITHMETIC HAPPENS, exactly as it does in the
  // withdrawal dialog: a cost takes the smaller of what it still needs and what
  // the withdrawal still has. A cost that would take nothing is not ticked at
  // all — there is no money left for it, and letting it tick would send a
  // request the server has to refuse.
  const tick = (it, checked, setList) => {
    if (!checked) {
      setGrants((p2) => { const n = { ...p2 }; delete n[it.id]; return n; });
      delete factAtTick.current[it.id];
      setList((p2) => p2.filter((x) => x !== it.id));
      return;
    }
    const give = grantUsd(it, roomLeft);
    if (give <= 0) return;              // nothing left to give this one
    setGrants((p2) => ({ ...p2, [it.id]: give }));
    factAtTick.current[it.id] = factOfRow(it);
    setList((p2) => [...p2, it.id]);
  };
  // ...AND A ROW THE SERVER WILL NOT LET GROW IS NOT OFFERED. (ZEN-170)
  //
  // Asking for this distribution's own matches back is what makes the amount a
  // total, but it also brings back a row that may be KEPT and never increased —
  // a cost held over from another calendar year. Its ceiling is the whole expense,
  // so it looked like ordinary headroom, and ticking it failed the ENTIRE click
  // with a refusal, taking every unrelated cost selected alongside it down too.
  // The server answers this, because the server owns the rule.
  // ...AND THE SAME FAIL-CLOSED READING THE DIALOG USES. `may_increase` alone was
  // enough while every server answering this endpoint knew the year rule. It is
  // not enough for the few minutes after a release when this bundle can be newer
  // than the server it is talking to: an older server omits `selectable`
  // entirely AND answers `may_increase: true` for a cost paid in another
  // calendar year, because it has no year rule for paid costs yet. Read as
  // permission, this panel would offer that cost and the old server would accept
  // it. An absent flag means NO here exactly as it does in the dialog — two
  // pickers, one rule, one reading. (ZEN-173, pre-merge diff review round 3.)
  // The two flags are now read INSIDE the shared rule, so this filter says the
  // same thing twice. Left standing deliberately: it is free, and it keeps the
  // fail-closed reading visible at the place a reader looks for it.
  const items = (preview?.items || []).filter(
    (it) => newMoney(it) > 0.005 && it.selectable === true && it.may_increase !== false);
  const rateWait = rateWaitRows(preview?.items);

  // ── Costs paid in a DIFFERENT calendar year (ZEN-174) ────────────────────
  //
  // THE THIRD DOOR. Best practice is to take a withdrawal in the same calendar
  // year the cost was paid, and the app nudges toward it — but there is no
  // statute behind that: two searches of IRS Publication 970 found no such rule
  // and IRC section 529 is silent on timing. Paying a university early and
  // having the plan reimburse you the following January is common and is not
  // forbidden, so the account owner can pair the two here with a short note
  // saying why. The note is kept inside the app for ever and never reaches the
  // tax packet (owner ruling, 2026-08-13).
  //
  // SHOWN SEPARATELY, NOT MIXED IN. These are not ordinary picks — each one is a
  // deliberate decision that gets recorded — so they sit under their own heading
  // rather than looking like the rest of the list with a badge.
  //
  // FAIL-CLOSED, like every other flag this panel reads. The reason code must be
  // exactly the one the server sends for an already-paid cost in another year.
  // Anything else — an older server that omits it, a code this bundle has not
  // heard of — offers nothing, which is the same reading `selectable` and
  // `may_increase` already get one line up.
  // BOTH SHAPES OF THE SAME THING. A cross-year cost this distribution does not
  // hold yet arrives carrying the refusal code; one it ALREADY holds a share of
  // is `selectable` (it may be kept) and carries no code at all — so filtering on
  // the code alone dropped exactly the rows an owner wants to top up, and the
  // "grow it with a new note" path was unreachable here. The server's own
  // `may_increase_with_note` covers both. (Diff review, round 2.)
  const crossYear = (preview?.items || []).filter(
    (it) => (it.not_selectable_reason === "different_tax_year_paid"
             || it.may_increase_with_note === true)
      && newMoney(it) > 0.005);
  const [xSel, setXSel] = useState([]);
  const [xNotes, setXNotes] = useState({});
  const xDist = withdrawal.date_received || withdrawal.date_requested;
  // A draft is only a draft for the facts it was written against.
  const xNoteOf = (id) => {
    const held = xNotes[id];
    if (!held) return "";
    const row = crossYear.find((x) => x.id === id) || (preview?.items || []).find((x) => x.id === id);
    return held.facts === noteFacts(row, xDist) ? (held.note || "").trim() : "";
  };
  const xIncomplete = xSel.filter((id) => !xNoteOf(id));
  // The facts the ticked rows were measured against, as the server last stated
  // them. When any of them moves, what is held here describes a state that no
  // longer exists — see the note beside the withdrawal-figures watch above.
  // WHAT EACH ROW SAID WHEN IT WAS TICKED, against what it says now.
  //
  // COMPARED PER ROW, NOT AS ONE SIGNATURE OF THE SELECTION. The first version
  // hashed the picked ids together with their facts — so ticking changed the
  // hash, the watch fired, and it threw away the tick that had just been made.
  // Every case in the panel's own test file went red at once, which is the only
  // reason it did not ship. Recording each row's facts AT TICK TIME and
  // comparing them later has no such loop: ticking writes the value the
  // comparison then agrees with.
  const stale = [...sel, ...xSel].some((id) => {
    const recorded = factAtTick.current[id];
    if (recorded === undefined) return false;
    return factOfRow([...items, ...crossYear].find((x) => x.id === id)) !== recorded;
  });
  useEffect(() => {
    if (!stale) return;
    setSel([]); setXSel([]); setXNotes({}); setGrants({});
    factAtTick.current = {};
  }, [stale]);

  // WHAT THE BUTTON PROMISES IS WHAT THE SAVE WILL DO. It used to add up each
  // cost's own room, which on a withdrawal with less money than that overstated
  // the total and then failed. (ZEN-380)
  const selTotal = grantedSoFar;
  // WHAT A SAVE WOULD SEND, built once: the save and the live allowance check
  // send exactly the same thing. (ZEN-842)
  const allocBody = () => {
    const picked = [...sel, ...xSel];
    // A TOTAL, NOT AN INCREMENT — what this withdrawal will hold against the cost
    // once saved. It used to send the cost's whole remaining room, which ignored
    // how much of the withdrawal was left. Now it is what the row already holds
    // plus what this click was granted. (ZEN-380)
    const allocations = [...items, ...crossYear].filter((it) => picked.includes(it.id))
      .map((it) => ({ expense_id: it.id,
                      amount_usd: Math.round((heldUsd(it) + (grants[it.id] || 0)) * 100) / 100 }));
    // The note travels in the SAME save that creates the match, which is what
    // makes it a record of the decision rather than a comment added afterwards.
    const cross_year_overrides = xSel.map((id) => ({
      expense_id: id, note: xNoteOf(id),
      // The facts this screen showed. Compared, never stored.
      observed_expense_date: (crossYear.find((x) => x.id === id) || {}).transaction_date,
      observed_payee: (crossYear.find((x) => x.id === id) || {}).payee,
      observed_expense_usd: (crossYear.find((x) => x.id === id) || {}).base_usd,
      observed_distribution_date: withdrawal.date_received || withdrawal.date_requested }));
    return { expense_ids: picked, allocations, ...(cross_year_overrides.length ? { cross_year_overrides } : {}) };
  };
  // PAST THE SCHOOL'S ALLOWANCE: the same red note and tick box as the
  // withdrawal form, shown from the save's refusal. (ZEN-842)
  const _picked = allocBody();
  const [allowanceRisk, noteAllowanceRefusal, allowanceTick, setAllowanceTick] = useAllowanceRefusal(open && _picked.expense_ids.length > 0
    ? { student_id: withdrawal.student_id, editing_withdrawal_id: withdrawal.id, adds_to_withdrawal: true,
        allocations: _picked.allocations }
    : null);
  const apply = async () => {
    if (!tickCovers(allowanceRisk, allowanceTick)) { toast.error("Tick the box under the red note to save."); return; }
    const ack = ackToSend(allowanceRisk, allowanceTick);
    try {
      const { data } = await api.post(`/withdrawals/${withdrawal.id}/allocate`,
        { ...allocBody(), ...(ack != null ? { allowance_ack_usd: ack } : {}) });
      // A SAVE THAT MATCHED FEWER COSTS THAN SHE PICKED IS NOT A SUCCESS.
      //
      // The server has always said which ones it could not match, and in a
      // sentence written for a person — and no screen has ever read it, so a
      // partial save closed with a green tick and she would only find out by
      // reopening and counting. This change adds new reasons for that to happen
      // (a cost whose date moved under the request, a reason that could not be
      // recorded), so leaving the silence in place is no longer somebody else's
      // problem. The panel stays open, on the server's own words. (ZEN-273.)
      if (data && (data.label_pending_expense_ids || []).length > 0) {
        // The match saved but the year did not move — say so, or the owner
        // believes the cost now counts toward this year. (ZEN-331, round 3.)
        toast.warning(data.label_pending_note ||
          "The match was recorded, but the cost still counts toward the year it was paid. Open the expense to move its year.");
      }
      if (data && (data.unmatched_expense_ids || []).length > 0) {
        toast.error(data.unmatched_note || "Some costs could not be matched.");
        // THE PANEL STAYS OPEN, SO WHAT IT IS HOLDING MUST BE LET GO. (ZEN-380)
        //
        // Some of what was ticked HAS been written, so refreshing moves the
        // withdrawal's tied total forward — and a grant kept from before that
        // refresh is then counted twice, once inside the new total and once
        // here. Measured: a $2,500 withdrawal, $2,100 written, the button still
        // offering $2,500, and pressing it again asking for $4,600 and being
        // refused outright. The screen invited the retry that fails.
        //
        // Cleared rather than adjusted: what survived the save is now on the
        // record and comes back in the refreshed list, so re-picking against
        // the true figures is the only honest starting point.
        setSel([]); setXSel([]); setXNotes({}); setGrants({});
        onDone();
        return;
      }
      toast.success("Tied to 529 distribution");
      setOpen(false); setSel([]); setXSel([]); setXNotes({}); setGrants({}); onDone();
    }
    catch (e) {
      // Held for the tick: show the red note and tick box from the refusal.
      if (noteAllowanceRefusal(e)) { setAllowanceTick(null); toast.error("Tick the box under the red note to save."); return; }
      toast.error(apiErr(e));
    }
  };
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-[#034AA2] hover:underline" data-testid={`alloc-toggle-${withdrawal.id}`}>{open ? "Hide" : "Tie eligible expenses ▾"}</button>
      {open && (
        <div className="mt-2 space-y-1 rounded-md border border-[#C2C9D1] p-2">
          <div className="mb-1 border-b border-[#E9E9EB] pb-1 text-[11px] font-semibold text-[#6b7280]"
               data-testid={`alloc-room-${withdrawal.id}`}>
            {roomLeft > 0.005
              ? `${money(roomLeft, "USD")} of this withdrawal is still unallocated.`
              : "This withdrawal is fully allocated."}
          </div>
          {preview?.unpriced_count > 0 && (
            <div className="rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid={`alloc-not-shown-no-dollar-amount-${withdrawal.id}`}>
              {notShownNoDollarAmount(preview.unpriced_count)}
            </div>
          )}
          {items.length === 0 && rateWait.length === 0 ? <div className="text-xs text-[#6b7280]">No untied eligible expenses available.</div> : items.map((it) => (
            <label key={it.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`alloc-row-${it.id}`}>
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={sel.includes(it.id)}
                  disabled={!sel.includes(it.id) && grantUsd(it, roomLeft) <= 0}
                  onChange={(ev) => tick(it, ev.target.checked, setSel)} />
                {fmtDate(it.transaction_date)} · {it.payee}
                {it.is_future && <span className="rounded-full bg-[#E9E9EB] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#6b7280]">future</span>}
                {it.fx_basis === "withdrawal_date" && <span className="rounded-full bg-[#D97706]/15 px-1.5 py-0.5 text-[9px] font-semibold text-[#D97706]">FX @ distribution</span>}
              </span>
              <span className="tnum font-semibold">{sel.includes(it.id) && (grants[it.id] || 0) < newMoney(it) - 0.005
                  ? `${money(grants[it.id] || 0, "USD")} of ${money(newMoney(it), "USD")}`
                  : money(sel.includes(it.id) ? (grants[it.id] || 0) : newMoney(it), "USD")}</span>
            </label>
          ))}
          <RateWaitRows items={rateWait} testid={`alloc-rate-wait-${withdrawal.id}`} />
          {crossYear.length > 0 && (
            <div className="mt-3 rounded-md border border-[#D97706]/30 bg-[#FCF5EE] p-2" data-testid={`alloc-crossyear-${withdrawal.id}`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#B45309]">Paid in a different calendar year</div>
              <p className="mt-1 text-[11px] leading-snug text-[#6b7280]">
                Best practice is to take a withdrawal in the same calendar year the cost was paid. Pairing these is allowed &mdash; the tax code does not set a deadline &mdash; but the account owner has to say why. The note stays in this app and never appears in your tax packet.
              </p>
              <div className="mt-2 space-y-2">
                {crossYear.map((it) => {
                  const ticked = xSel.includes(it.id);
                  return (
                    <div key={it.id} className="rounded border border-[#D97706]/20 bg-white p-2" data-testid={`alloc-crossyear-row-${it.id}`}>
                      <label className="flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="flex min-w-0 flex-wrap items-center gap-2">
                          <input type="checkbox" checked={ticked}
                            disabled={!ticked && grantUsd(it, roomLeft) <= 0}
                            onChange={(ev) => tick(it, ev.target.checked, setXSel)}
                            data-testid={`alloc-crossyear-check-${it.id}`} />
                          <span className="break-words">{fmtDate(it.transaction_date)} &middot; {it.payee}</span>
                          <span className="whitespace-nowrap rounded-full bg-[#D97706]/15 px-1.5 py-0.5 text-[9px] font-semibold text-[#B45309]">paid {it.paid_year}</span>
                        </span>
                        <span className="tnum font-semibold">{ticked && (grants[it.id] || 0) < newMoney(it) - 0.005
                          ? `${money(grants[it.id] || 0, "USD")} of ${money(newMoney(it), "USD")}`
                          : money(ticked ? (grants[it.id] || 0) : newMoney(it), "USD")}</span>
                      </label>
                      {ticked && (
                        <label className="mt-2 block">
                          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Why do these belong together?</span>
                          <textarea rows={2} maxLength={2000} value={(xNotes[it.id] || {}).note || ""}
                            onChange={(ev) => setXNotes((p) => ({ ...p, [it.id]: { note: ev.target.value, facts: noteFacts(it, xDist) } }))}
                            placeholder="For example: paid the university early for next year's study."
                            className="w-full rounded border border-[#C2C9D1] px-2 py-1 text-xs"
                            data-testid={`alloc-crossyear-note-${it.id}`} />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {xIncomplete.length > 0 && (
            <div className="mt-2 text-[11px] font-semibold text-[#B45309]" data-testid={`alloc-crossyear-needs-note-${withdrawal.id}`}>
              Add a note to {xIncomplete.length === 1 ? "the cost you ticked above" : `all ${xIncomplete.length} costs you ticked above`} before saving.
            </div>
          )}
          <PastAllowanceTick risk={allowanceRisk} tickedFor={allowanceTick} onTick={setAllowanceTick}
            testid={`alloc-past-allowance-${withdrawal.id}`} />
          {(sel.length > 0 || xSel.length > 0) && (
            <Btn variant="green" className="mt-1 h-8" onClick={apply}
              disabled={xIncomplete.length > 0 || !tickCovers(allowanceRisk, allowanceTick)}
              data-testid={`alloc-apply-${withdrawal.id}`}>Tie {sel.length + xSel.length} &middot; {money(selTotal, "USD")}</Btn>
          )}
        </div>
      )}
    </div>
  );
}

// The paperwork of ONE 529 distribution. Removing a document here really
// deletes it — a tax record — so this screen keeps its own warning and its own
// delete; the shared box only lists, opens and takes files. Exported so that
// rule can be proven by pressing the bin. (ZEN-445)
export function DistributionDocuments({ withdrawal, files, onFilesChange, recordVersion, onRecordVersion, onSaved }) {
  const [deleteDocId, setDeleteDocId] = useState(null);
  const stored = (withdrawal?.documents || []).map((d) => ({
    id: d.id, label: d.filename, href: withdrawalDocUrlById(withdrawal.id, d.id),
  }));
  return (
    <>
      <DocumentsBox existing={stored} onRemove={setDeleteDocId} files={files} onFilesChange={onFilesChange} accept={DOCUMENT_ACCEPT} testid="wd" />
      <DeleteDialog
        open={!!deleteDocId}
        onClose={() => setDeleteDocId(null)}
        onConfirm={async () => { try { const { data } = await api.delete(`/withdrawals/${withdrawal.id}/documents/${deleteDocId}`, { params: recordVersion != null ? { record_version: recordVersion } : {} }); if (data && data.record_version != null) onRecordVersion(data.record_version); setDeleteDocId(null); onSaved(); } catch (e) { toast.error(apiErr(e)); setDeleteDocId(null); } }}
        noun="document"
        testid="wd-doc-delete"
      />
    </>
  );
}

function WithdrawalDialog({ open, onClose, onSaved, studentId, fromFigure, editingWithdrawal, untied = [], onTieUntied }) {
  // The same save pressed twice records once: one key per opening of this
  // form, sent with every press of Save in that opening. (ZEN-272; lib/saveOnce.js)
  const saveKey = useSaveKey(`${open}:${editingWithdrawal?.id || "new"}`);
  const { data: funds = [] } = useQuery({ queryKey: ["funds", studentId], queryFn: async () => (await api.get(`/fund-accounts?student_id=${studentId}&include_archived=${editingWithdrawal ? "true" : "false"}`)).data, enabled: open });
  const { data: dlgStudents = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data, enabled: open });
  // The 529 dropdown renders the same computed label as everywhere else
  // (ZEN-4: beneficiary initials at the very end), with the provider alongside
  // — it distinguishes two plans at different institutions at a glance.
  const dlgBenName = dlgStudents.find((s) => s.id === studentId)?.full_name || "";
  const fundOptionLabel = (x) => {
    const computed = psLabel({ type: "529_plan", state: x.state, last4: fundLast4(x) },
      dlgOwnerName, dlgBenName);
    return computed ? `${computed} · ${x.provider}` : `${x.provider} ${x.label || ""}`.trim();
  };
  const fundChoices = planChoices(funds, editingWithdrawal?.fund_account_id);
  const { data: parties } = useParties();
  const dlgOwnerName = parties?.owner?.full_name || parties?.owner?.nickname || "";
  const { data: tenantSettings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { onDateValidation, hasDateErrors } = useDateErrors();
  const [f, setF] = useState({
    distribution_type: "qualified_education",
    mode: "reimbursement", expense_group: "post_secondary", date_requested: localISO(), amount: "", fund_account_id: "",
    intended_recipient: "student", actual_recipient: "student", actual_recipient_party_id: "", actual_recipient_other: "",
    actual_recipient_confirmed: false, actual_recipient_note: "",
    balance_prior: "", balance_prior_date: "",
    date_received: "", notes: "", linked_expense_ids: [],
    loan_servicer: "", roth_account_note: "", trustee_to_trustee: true,
  });
  const set = (k, v) => setF((p) => {
    const next = { ...p, [k]: v };
    // Auto-fill balance_prior_date with today when user first enters a balance amount
    if (k === "balance_prior" && v && !p.balance_prior_date) {
      next.balance_prior_date = localISO();
    }
    // ...and take it away when the owner ACTIVELY clears a balance they could
    // see. The date only means "when this balance was read", so a deliberate
    // clear should take it too. Guarded on the previous value so that merely
    // opening a record that already had a stranded date — possibly the last
    // trace of a balance lost in migration — never counts as clearing it.
    if (k === "balance_prior" && !v && p.balance_prior) {
      next.balance_prior_date = "";
    }
    return next;
  });
  const [skipExpenses, setSkipExpenses] = useState(false);

  // THE YEAR FOLLOWS THE DATES, and it derives from a date the SERVER would also
  // accept — not from any non-empty string. The server treats a received date it
  // cannot read as unanswerable rather than falling back to the requested date,
  // and refuses one in the future outright; a looser rule here would put money
  // guidance on screen computed from a value the server rejects. When there is
  // no answer, everything keyed to the year shows nothing rather than something
  // wrong. (ZEN-172)
  const derivedTaxYear = React.useMemo(() => {
    const readable = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.slice(0, 10))
      && !isNaN(new Date(`${v.slice(0, 10)}T00:00:00Z`).getTime()) ? v.slice(0, 10) : null);
    const received = f.date_received;
    if (received !== undefined && received !== null && received !== "") {
      const d = readable(received);
      if (!d) return null;                                   // present but unreadable
      if (d > new Date().toISOString().slice(0, 10)) return null;   // a receipt date in the future
      return Number(d.slice(0, 4));
    }
    const requested = readable(f.date_requested);
    return requested ? Number(requested.slice(0, 4)) : null;
  }, [f.date_received, f.date_requested]);

  const { data: capData } = useQuery({
    queryKey: ["dash-student", studentId, derivedTaxYear],
    queryFn: async () => (await api.get(`/dashboard/student/${studentId}?tax_year=${derivedTaxYear}`)).data,
    enabled: open && !!studentId && !!derivedTaxYear,
  });
  // The older red "Exceeds 529 Cost-of-Attendance cap" box read the allowance
  // alone and only once rent was already over; the red note and tick box below
  // (PastAllowanceTick) replace it. (Maryse, ZEN-842, 3 Nov 2026: "1.agree".)
  const [docFiles, setDocFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  // WHICH VERSION OF THE RECORD THIS DIALOG IS SHOWING. (ZEN-182)
  //
  // HELD IN STATE, NOT READ OFF THE PROP, because this dialog CHANGES the record
  // while it is open: deleting a receipt is a write, and it moves the count. The
  // prop is whatever the list held when the dialog opened, so saving after a
  // delete sent a count that was one behind and the account owner was refused
  // by a guard her own action had tripped — told somebody else had edited it.
  // Found by adversarial diff review, round 1.
  const [recordVersion, setRecordVersion] = useState(null);
  React.useEffect(() => {
    if (open) setRecordVersion(editingWithdrawal ? editingWithdrawal.record_version : null);
  }, [open, editingWithdrawal]);

  // Pre-fill from existing withdrawal when editing, or reset to blank for new
  React.useEffect(() => {
    if (!open) { setDocFiles([]); setSkipExpenses(false); return; }
    if (editingWithdrawal) {
      setF({
        distribution_type: editingWithdrawal.distribution_type || "qualified_education",
        mode: editingWithdrawal.mode || "reimbursement",
        expense_group: editingWithdrawal.expense_group || "post_secondary",
        date_requested: editingWithdrawal.date_requested || localISO(),
        amount: String(editingWithdrawal.amount ?? ""),
        fund_account_id: editingWithdrawal.fund_account_id || "",
        intended_recipient: editingWithdrawal.intended_recipient || "student",
        actual_recipient: editingWithdrawal.actual_recipient || "student",
        actual_recipient_party_id: editingWithdrawal.actual_recipient_party_id || "",
        actual_recipient_other: editingWithdrawal.actual_recipient_other || "",
        actual_recipient_confirmed: editingWithdrawal.actual_recipient_confirmed || false,
        actual_recipient_note: editingWithdrawal.actual_recipient_note || "",
        balance_prior: editingWithdrawal.balance_prior != null ? String(editingWithdrawal.balance_prior) : "",
        // Load the stored date VERBATIM, even with no balance beside it.
        // Blanking it here looked like the fix for showing a date that stands
        // for nothing — but this form submits the value on save, so any
        // unrelated edit would have erased it. Where that date is all that
        // survives of a balance lost in the Emergent migration, it is recovery
        // evidence. It is hidden at the input instead, never discarded.
        balance_prior_date: editingWithdrawal.balance_prior_date
          || (editingWithdrawal.balance_prior != null ? localISO() : ""),
        date_received: editingWithdrawal.date_received || "",
        notes: editingWithdrawal.notes || "",
        linked_expense_ids: [],  // Will be populated from eligible-preview's allocated_to_editing
        loan_servicer: editingWithdrawal.loan_servicer || "",
        roth_account_note: editingWithdrawal.roth_account_note || "",
        trustee_to_trustee: editingWithdrawal.trustee_to_trustee ?? true,
      });
      setAllocAmts({});  // Will be populated from preview data
      prevTaxYearRef.current = editingWithdrawal.eligibility_tax_year || new Date().getFullYear();
      skipTaxYearWipe.current = true;
      amountLocked.current = !!(editingWithdrawal.amount);
      preInit.current = true; // skip auto-prefill for edit mode
      // AN UNSAVED NOTE BELONGS TO THE WITHDRAWAL IT WAS TYPED FOR, and nothing
      // else. Cleared only on a NEW withdrawal, a half-written reason for one
      // withdrawal could be carried into the next dialog and saved as a
      // different one's permanent reason — a record of a decision she never
      // made about money she was not looking at. (Diff review, round 13.)
      setCrossYearNotes({});
    } else {
      // Reset to blank defaults for a new withdrawal
      setF({
        distribution_type: "qualified_education",
        mode: "reimbursement", expense_group: "post_secondary", date_requested: localISO(), amount: "", fund_account_id: funds[0]?.id || "",
        intended_recipient: "student", actual_recipient: "student", actual_recipient_party_id: "", actual_recipient_other: "",
        actual_recipient_confirmed: false, actual_recipient_note: "",
        balance_prior: "", balance_prior_date: "",
        date_received: "", notes: "", linked_expense_ids: [],
        loan_servicer: "", roth_account_note: "", trustee_to_trustee: true,
      });
      setDocFiles([]);
      setCrossYearNotes({});
    }
  }, [open, editingWithdrawal?.id]); // eslint-disable-line

  React.useEffect(() => {
    if (open && funds[0] && !editingWithdrawal && !f.fund_account_id) set("fund_account_id", funds[0].id);
  }, [open, funds]); // eslint-disable-line

  const { data: preview } = useQuery({
    // TWO DATES, TWO JOBS — and BOTH belong in the key. `as_of_date` fixes the
    // exchange-rate basis; the effective date (received, falling back to
    // requested) decides the TAX YEAR, and therefore which expenses that have not
    // been paid yet may be funded ahead. They are equal on almost every record,
    // which is exactly why sending one for both would look right until a
    // withdrawal requested in December is received in January.
    //
    // date_received IS IN THE KEY. Without it, changing the received date left
    // this list offering expenses the save would then refuse. (ZEN-158)
    //
    // ...AND SO IS THE CLICK, when the form was opened from a figure: a key no
    // earlier request can share, so the selection is built only from an answer
    // fetched for this opening, never from a remembered one. (ZEN-582)
    queryKey: ["wd-eligible-preview", studentId, f.date_requested, f.date_received, editingWithdrawal?.id, fromFigure?.askedAt],
    queryFn: async () => (await api.get(`/withdrawals/eligible-preview?student_id=${studentId}&as_of_date=${f.date_requested}&distribution_date=${f.date_received || f.date_requested}${editingWithdrawal ? `&editing_withdrawal_id=${editingWithdrawal.id}` : ""}`)).data,
    enabled: open && !!studentId && !!f.date_requested,
  });
  const previewItems = React.useMemo(() => preview?.items || [], [preview]);

  // In edit mode, initialize selection from the expense-side truth (allocated_to_editing)
  const editPreSelectDone = React.useRef(false);
  React.useEffect(() => {
    if (!editingWithdrawal || !preview?.items?.length || editPreSelectDone.current) return;
    const items = preview.items;
    const tiedIds = [];
    const tiedAmts = {};
    for (const it of items) {
      if (it.allocated_to_editing > 0.005) {
        tiedIds.push(it.id);
        tiedAmts[it.id] = it.allocated_to_editing;
      }
    }
    if (tiedIds.length > 0 || f.linked_expense_ids.length === 0) {
      setF((prev) => ({ ...prev, linked_expense_ids: tiedIds }));
      setAllocAmts(tiedAmts);
      editPreSelectDone.current = true;
    }
  }, [preview?.items, editingWithdrawal]); // eslint-disable-line
  React.useEffect(() => { if (!open) editPreSelectDone.current = false; }, [open]);
  // Track initial tied IDs for untie safety net
  const initialTiedIds = React.useRef([]);
  React.useEffect(() => {
    if (editingWithdrawal && preview?.items?.length && initialTiedIds.current.length === 0) {
      initialTiedIds.current = preview.items.filter((it) => it.allocated_to_editing > 0.005).map((it) => it.id);
    }
    if (!open) initialTiedIds.current = [];
  }, [open, preview?.items, editingWithdrawal]); // eslint-disable-line

  // Filter to only expenses belonging to the withdrawal's tax year (by eligibility_tax_year, not payment date)
  // ...EXCEPT AN EXPENSE THE SERVER HAS ALREADY SAID MAY BE DRAWN AHEAD. Pre-funding
  // turns on the calendar year the money moves in; this filter asks which tax
  // year a cost is attributed to. They agree most of the time, and where the
  // owner has deliberately set them apart this list was hiding an expense the same
  // response had just marked drawable — the owner would never have known it was
  // there to pick. The server decides; this list does not re-derive it.
  // (Diff review, round 2.)
  const taxYearStr = String(derivedTaxYear ?? "");
  // WHICH COSTS MAY BE TICKED IS THE SERVER'S ANSWER, NOT THIS SCREEN'S.
  //
  // This list used to compare the tax year stored on the cost against the year
  // the withdrawal is filed under. That comparison WAS the same-calendar-year
  // rule for a cost already paid — the server had no such rule for paid costs —
  // so a replayed request, a stale tab or any other caller wrote the pairing
  // unopposed. The rule now lives in distribution_backing and arrives as a
  // flag. (ZEN-173)
  //
  // A MISSING FLAG IS READ AS "NO", NEVER AS "FINE". For a few minutes after a
  // release the browser bundle can be newer than the server (the bundle ships on
  // push, the backend follows on a poller), and answering "fine" there would
  // offer costs the server is about to start refusing.
  //
  // ...AND A COST THIS WITHDRAWAL IS ALREADY MATCHED TO IS ALWAYS SHOWN.
  // Hiding one does not leave it alone: the dialog rebuilds its ticked list
  // from what is visible, so an existing match filtered out here was
  // unticked by an edit that had nothing to do with it, and saving a note
  // would have quietly removed money the owner had matched. The same
  // destructive-by-omission shape the writer's keeping-exception exists to
  // prevent, one layer up. (Diff review, round 5.)
  const filteredPreviewItems = React.useMemo(() =>
    previewItems.filter((it) => it.selectable === true
      || (it.allocated_to_editing || 0) > 0.005),
    [previewItems]
  );
  // SHOWN, NOT HIDDEN. A cost paid in another calendar year cannot back this
  // withdrawal, but making it disappear tells the owner nothing — they go
  // looking for a receipt they know exists. It is listed, explained, and not
  // tickable. (ZEN-173)
  const priorYearItems = React.useMemo(() =>
    previewItems.filter((it) => it.not_selectable_reason === "different_tax_year_paid"),
    [previewItems]
  );
  // PAID AFTER THE MONEY CAME OUT — refused entirely, and still shown.
  // (ZEN-331, owner ruling 2026-08-26: a withdrawal is never justified by a
  // cost paid in a later calendar year; no note makes it allowed.) Hiding the
  // row would send the owner hunting for a receipt she knows exists, so it is
  // listed with the true sentence and no tick-box.
  const laterYearItems = React.useMemo(() =>
    previewItems.filter((it) => it.not_selectable_reason === "different_tax_year_paid_later"),
    [previewItems]
  );
  // The withdrawal's own date could not be read, so nothing can be compared to
  // it. That is one fact about the withdrawal, not a property of each cost, so
  // it gets a sentence rather than a list.
  // Waiting only for a final exchange rate: listed, never tickable. (ZEN-834)
  const rateWaitItems = React.useMemo(() => rateWaitRows(previewItems), [previewItems]);
  const unmatchableItems = React.useMemo(() =>
    previewItems.filter((it) => it.not_selectable_reason === "tax_year_unestablished"),
    [previewItems]
  );
  // A page newer than the server it is talking to. Not a version negotiation —
  // just the honest reading of a response that predates the flag.
  const serverPredatesTheFlag = previewItems.length > 0
    && previewItems.every((it) => it.selectable === undefined);

  // ── The account owner's notes for costs paid in another calendar year ─────
  //
  // THIS DIALOG, NOT ONLY THE TIE PICKER, and that distinction is the whole
  // reason this is here. The picker only renders for a LUMP-SUM distribution;
  // the case this feature exists for — paying a university yourself and having
  // the plan reimburse you the following January — is a REIMBURSEMENT, which is
  // created and edited here. Building the door on the other flow only would
  // have shipped a feature the account owner could not reach, with every test
  // green. (Found by driving the real screen, not by review.)
  const [crossYearNotes, setCrossYearNotes] = useState({});
  const cyDist = f.date_received || f.date_requested;
  // A draft is only a draft for the facts it was written against. See noteFacts.
  const crossYearNoteOf = (id) => {
    const held = crossYearNotes[id];
    if (!held) return "";
    const row = previewItems.find((x) => x.id === id);
    return held.facts === noteFacts(row, cyDist) ? (held.note || "").trim() : "";
  };

  const remById = React.useMemo(() => Object.fromEntries(filteredPreviewItems.map((it) => [it.id, it.withdraw_usd])), [filteredPreviewItems]); // eslint-disable-line
  // ONE CEILING FOR WHAT A ROW MAY ASK FOR, and the server decides it. A cost
  // held over from another calendar year may keep the amount already matched to
  // it and not a cent more, so its ceiling is that amount — not what the expense
  // comes to. Every path that puts a number into allocAmts goes through here;
  // computing it at each site is how three of the four came to offer money the
  // save refuses, which fails the WHOLE save on one row. (ZEN-173; the rest of
  // this dialog's amount handling is ZEN-190.)
  // THE SHARED RULE AGAIN — same file, same answer as the picker above and as
  // the Overview's headline figure. What used to be here was a third hand-rolled
  // version of it: `may_increase_with_note` unlocks the whole expense, a row that
  // may not grow is fixed at what it holds, everything else takes the expense. That
  // is exactly what ceilingUsd computes, with one difference that matters — it
  // also refuses a row the server has not marked selectable, which this copy did
  // not, so against a server older than this page the dialog could offer a cost
  // the save would refuse. (ZEN-190)
  const ceilingFor = React.useCallback((it) => ceilingUsd(it), []);
  // BOTH LISTS. A cost paid in another calendar year is now tickable (ZEN-174),
  // and `toggleExp` reads this map to work out what amount to put against a row
  // it has just ticked. Built from the ordinary list alone, a cross-year row
  // computes `undefined`, falls through the "no room left" guard, and silently
  // refuses to tick — the feature present and unusable.
  const ceilingById = React.useMemo(
    () => Object.fromEntries([...filteredPreviewItems, ...priorYearItems]
      .map((it) => [it.id, ceilingFor(it)])),
    [filteredPreviewItems, priorYearItems, ceilingFor]);
  const [allocAmts, setAllocAmts] = useState({});

  // ── The account owner's notes for costs paid in another calendar year ─────
  //
  // DECLARED BELOW `allocAmts`, and that is not tidiness. `isGrowing` compares
  // the figure being asked for against what the row already holds, so it reads
  // `allocAmts` — and a `const` cannot be read above its own declaration. Placed
  // higher, the whole page threw on first render and rendered nothing at all:
  // no error boundary, no message, just a blank screen. Neither the linter nor
  // any test caught it; loading the page did. (ZEN-174)
  // EVERY ROW A NOTE WOULD UNLOCK, not only the ones in the cross-year list.
  //
  // A cost from another calendar year that this withdrawal ALREADY holds a share
  // of is `selectable` — it may be KEPT — so it appears in the ordinary list,
  // not the cross-year one. Growing it still needs a note, and reading only the
  // cross-year list meant the account owner could tick it up, be offered no
  // note box, and lose the whole save to a refusal she had no way to satisfy.
  // The server's flag decides membership, not which list a row happened to land
  // in. (Diff review, round 1.)
  const needsNote = React.useCallback((it) => !!it && it.may_increase_with_note === true, []);
  // ...AND ONLY WHEN THE ROW IS ACTUALLY BEING GROWN.
  //
  // Re-saving a withdrawal without touching a cross-year cost asks for nothing
  // new, and the rules allow that unconditionally — keeping a match has never
  // needed a reason, only creating or enlarging one does. Demanding a note there
  // would block an owner from editing a note or a date on a withdrawal she
  // settled months ago, which is the "a guard that refuses correct edits" mistake
  // this codebase has already paid for twice. The comparison is against what the
  // row ALREADY holds on this withdrawal. (Diff review, round 2.)
  const isGrowing = React.useCallback((it) => {
    if (!needsNote(it)) return false;
    const held = Number(it.allocated_to_editing) || 0;
    const asking = Number(allocAmts[it.id]);
    if (!Number.isFinite(asking)) return true;   // no figure yet means "as much as it needs"
    return asking > held + 0.005;
  }, [needsNote, allocAmts]);
  const crossYearPicked = React.useMemo(
    () => previewItems.filter((it) => isGrowing(it) && f.linked_expense_ids.includes(it.id))
      .map((it) => it.id),
    [previewItems, f.linked_expense_ids, isGrowing]);
  const crossYearMissingNote = crossYearPicked.filter((id) => !crossYearNoteOf(id));
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const sumOf = (ids, amts) => round2(ids.reduce((s, id) => s + (Number(amts[id]) || 0), 0));
  const selTotal = sumOf(f.linked_expense_ids, allocAmts);
  // THE MATCHES THIS SAVE WOULD SEND, built once: the save and the live
  // allowance check below send exactly the same thing. (ZEN-842)
  const matchFields = () => {
    const isQ = f.distribution_type === "qualified_education";
    if (skipExpenses || !isQ) return { linked_expense_ids: [], allocations: [] };
    return {
      linked_expense_ids: f.linked_expense_ids,
      allocations: f.linked_expense_ids.map((id) => ({ expense_id: id, amount_usd: Number(allocAmts[id]) || 0 })),
      // IN THE SAME SAVE THAT CREATES THE MATCH — which is what makes the note a
      // record of the decision rather than a comment added afterwards. Omitted
      // entirely when there is nothing to say, so an ordinary save is unchanged.
      ...(crossYearPicked.length === 0 ? {} : {
        cross_year_overrides: crossYearPicked.map((id) => ({ expense_id: id, note: crossYearNoteOf(id),
          // The facts this screen showed. Compared, never stored — a save made
          // after the cost moved underneath the page is refused rather than
          // recording a decision about something she was not shown. All three,
          // because the server hashes all three.
          observed_expense_date: (previewItems.find((x) => x.id === id) || {}).transaction_date,
          observed_payee: (previewItems.find((x) => x.id === id) || {}).payee,
          observed_expense_usd: (previewItems.find((x) => x.id === id) || {}).base_usd,
          // The withdrawal's own date as this form shows it — the one that
          // decides which calendar year the money falls in.
          observed_distribution_date: f.date_received || f.date_requested })),
      }),
    };
  };
  // PAST THE SCHOOL'S ALLOWANCE: the red note and tick box show when Save is
  // pressed and the server holds it with the figure ("Yes, on Save", Maryse,
  // 4 Nov 2026). The selection below only keys that figure; it is never sent.
  // (ZEN-842)
  const _amt = parseFloat(f.amount);
  const _match = matchFields();
  const allowanceSelection = open && studentId && Number.isFinite(_amt) && _match.linked_expense_ids.length > 0
    ? { student_id: studentId, editing_withdrawal_id: editingWithdrawal?.id || null, amount: _amt,
        date_requested: f.date_requested, date_received: f.date_received || null,
        distribution_type: f.distribution_type, allocations: _match.allocations }
    : null;
  const [allowanceRisk, noteAllowanceRefusal, allowanceTick, setAllowanceTick] = useAllowanceRefusal(allowanceSelection);
  const allowanceRoomNow = roomStillLeft(preview?.allowance_room, filteredPreviewItems,
    Object.fromEntries(f.linked_expense_ids.map((id) => [id, allocAmts[id]])));
  const preInit = React.useRef(false);
  const amountLocked = React.useRef(false);
  const [figureChanged, setFigureChanged] = useState(false);
  React.useEffect(() => { if (!open) { preInit.current = false; amountLocked.current = false; setAllocAmts({}); setFigureChanged(false); } }, [open]);

  // Clear expense selection when tax year changes (after initial load)
  const prevTaxYearRef = React.useRef(null);
  const skipTaxYearWipe = React.useRef(false);
  React.useEffect(() => {
    if (!open) { prevTaxYearRef.current = null; skipTaxYearWipe.current = false; return; }
    if (skipTaxYearWipe.current) {
      // Keep skipping until prevTaxYearRef matches the derived year
      prevTaxYearRef.current = derivedTaxYear;
      if (editingWithdrawal && String(derivedTaxYear) === String(editingWithdrawal.eligibility_tax_year)) {
        skipTaxYearWipe.current = false; // stable — stop skipping
      }
      return;
    }
    if (prevTaxYearRef.current !== null && prevTaxYearRef.current !== derivedTaxYear) {
      setAllocAmts({});
      setF((p) => ({ ...p, linked_expense_ids: [], amount: p.linked_expense_ids.length > 0 ? "" : p.amount }));
    }
    prevTaxYearRef.current = derivedTaxYear;
  }, [derivedTaxYear, open]); // eslint-disable-line

  const editInitApplied = React.useRef(false);
  React.useEffect(() => {
    if (!open || !preview || !editingWithdrawal) return;
    if (editInitApplied.current) return;
    // Re-apply the initial linked_expense_ids once preview resolves so checkboxes render correctly
    const initIds = ((editingWithdrawal.allocations || []).map(a => a.expense_id).filter(Boolean).length > 0
      ? (editingWithdrawal.allocations || []).map(a => a.expense_id).filter(Boolean)
      : (editingWithdrawal.linked_expense_ids || []));
    if (initIds.length > 0) {
      const previewIdSet = new Set(filteredPreviewItems.map(it => it.id));
      // Only keep IDs that exist in the preview (matching tax year)
      const validIds = initIds.filter(id => previewIdSet.has(id));
      if (validIds.length > 0) {
        const amts = {};
        for (const a of (editingWithdrawal.allocations || [])) {
          if (a.expense_id && previewIdSet.has(a.expense_id)) amts[a.expense_id] = a.amount_usd || 0;
        }
        // AND THEN EVERY ONE OF THEM IS HELD TO THE ROW'S CEILING.
        //
        // The amounts just read come from the distribution's OWN copy of its
        // match list, which this repo has already ruled is "a client echo that
        // nothing computes" — stored from a request body verbatim. The
        // authoritative figure is on the expense side and arrives as
        // allocated_to_editing. Where the two disagree, trusting the echo is
        // wrong in both directions: a stale HIGHER figure asks the server for
        // more than the row may hold, and one refusal fails the whole save; a
        // stale LOWER one quietly reduces a match nobody asked to reduce.
        //
        // So a row that may only KEEP what it holds takes the expense-side
        // figure outright, and every other row is capped at its ceiling. A row
        // that arrived with no amount at all falls back to the ceiling — it used
        // to fall back to the whole remaining expense, which on a cost held over
        // from another calendar year is an ask for more. (ZEN-173; pre-merge
        // diff review, round 1.)
        const _byId = Object.fromEntries(filteredPreviewItems.map((it) => [it.id, it]));
        for (const id of validIds) {
          const it = _byId[id];
          if (it && it.may_increase === false) amts[id] = it.allocated_to_editing || 0;
          else if (amts[id] == null) amts[id] = ceilingById[id] || 0;
          else amts[id] = Math.min(Number(amts[id]) || 0, ceilingById[id] || 0);
        }
        setAllocAmts((prev) => ({ ...prev, ...amts }));
        setF((p) => ({ ...p, linked_expense_ids: validIds }));
      }
    }
    editInitApplied.current = true;
  }, [preview, open, filteredPreviewItems.length]); // eslint-disable-line
  React.useEffect(() => { if (!open) editInitApplied.current = false; }, [open]);

  React.useEffect(() => {
    if (!open || editingWithdrawal) return;
    // The amount-only fill reads nothing from the list of eligible expenses,
    // so it never waits for it: a slow or failed list still fills the amount
    // (ZEN-791 review, round 3). Every other fill needs that list.
    if (!preview && !fromFigure?.amountOnly) return;
    if (!preInit.current && fromFigure?.amountOnly) {
      // OPENED FOR WHAT IS LEFT after the untied money (ZEN-791): the amount
      // only. Which expenses get the untied money, and which get this
      // withdrawal, is the owner's choice, so none is ticked for her.
      setAllocAmts({});
      setF((p) => ({ ...p, linked_expense_ids: [], amount: fromFigure.amountOnly }));
      preInit.current = true;
    } else if (!preInit.current && fromFigure) {
      // OPENED FROM A FIGURE: exactly the expenses behind it, each for what the
      // figure counted, or nothing at all. Ticking "most of them" would fill in
      // a different number from the one that was clicked — the defect itself.
      // (ZEN-582)
      const plan = planFill(filteredPreviewItems, fromFigure.rows, fromFigure.figure);
      if (plan) {
        setAllocAmts(plan.amts);
        setF((p) => ({ ...p, linked_expense_ids: plan.ids, amount: plan.amount }));
      } else {
        setAllocAmts({});
        setF((p) => ({ ...p, linked_expense_ids: [], amount: "" }));
        setFigureChanged(true);
      }
      preInit.current = true;
    } else if (!preInit.current) {
      // Auto-select current/past expenses, leave future ones unchecked
      const cutoff = (f.date_requested || "").slice(0, 10);
      const pastItems = filteredPreviewItems.filter((it) => !it.is_future && (it.transaction_date || "").slice(0, 10) <= cutoff);
      // Rent and food stop at the school's allowance (ZEN-842).
      const fits = allowanceRoomTracker(preview?.allowance_room);
      const amts = {};
      for (const it of pastItems) {
        const take = fits(it, ceilingById[it.id]);
        if (take > 0.005) amts[it.id] = take;
      }
      const ids = pastItems.map((it) => it.id).filter((id) => id in amts);
      setAllocAmts(amts);
      setF((p) => ({ ...p, linked_expense_ids: ids, amount: amountLocked.current ? p.amount : (p.amount || sumOf(ids, amts).toFixed(2)) }));
      preInit.current = true;
    }
  }, [preview, open]); // eslint-disable-line

  const autoFit = (val) => {
    set("amount", val);
    if (skipExpenses) return;
    const target = parseFloat(val);
    if (isNaN(target)) return;
    let rem = round2(target); const ids = []; const amts = {};
    // Rent and food stop at the school's allowance (ZEN-842); the rest of the
    // amount goes on to the next expense, oldest first, as before.
    const fits = allowanceRoomTracker(preview?.allowance_room);
    for (const it of filteredPreviewItems) {
      if (rem <= 0.005) break;
      const take = round2(fits(it, Math.min(ceilingFor(it), rem)));
      if (take <= 0.005) continue;
      ids.push(it.id); amts[it.id] = take; rem = round2(rem - take);
    }
    setAllocAmts(amts);
    setF((p) => ({ ...p, linked_expense_ids: ids, amount: val }));
  };

  const toggleExp = (id) => setF((p) => {
    const on = p.linked_expense_ids.includes(id);
    const ids = on ? p.linked_expense_ids.filter((x) => x !== id) : [...p.linked_expense_ids, id];
    const amts = { ...allocAmts };
    if (on) {
      delete amts[id];
    } else {
      // When amount is locked, cap the new expense so total doesn't exceed withdrawal amount
      const target = amountLocked.current ? (parseFloat(p.amount) || 0) : Infinity;
      const currentTotal = ids.filter((x) => x !== id).reduce((s, x) => s + (parseFloat(amts[x]) || 0), 0);
      const maxForThis = round2(Math.max(0, target - currentTotal));
      amts[id] = amountLocked.current ? Math.min(ceilingById[id], maxForThis) : ceilingById[id];
      // Don't add if there's no room left
      if (amountLocked.current && amts[id] <= 0.005) {
        delete amts[id];
        return { ...p, linked_expense_ids: p.linked_expense_ids }; // no change
      }
    }
    setAllocAmts(amts);
    if (amountLocked.current) return { ...p, linked_expense_ids: ids };
    return { ...p, linked_expense_ids: ids, amount: sumOf(ids, amts).toFixed(2) };
  });
  const setRowAmt = (id, val) => {
    // KEEP WHAT WAS TYPED WHILE IT IS BEING TYPED. This used to turn every
    // keystroke into a NUMBER, which was invisible while the box was a browser
    // number field and destroys a half-finished decimal now that it is text:
    // "4." parses to 4, the box re-renders as "4", and the next key makes 45 —
    // a ten-times-too-large allocation on a tax record, and the exact bug
    // accountingFormat.test.jsx was written for one field over.
    //
    // The ceiling still holds on every keystroke. It simply no longer rewrites
    // a value that is already under it. (ZEN-627)
    const n = parseFloat(val);
    const cap = ceilingById[id];
    const v = val === "" ? ""
      : (!isNaN(n) && n > cap) ? round2(cap)
      : val;
    const amts = { ...allocAmts, [id]: v };
    setAllocAmts(amts);
    if (!amountLocked.current) {
      setF((p) => ({ ...p, amount: sumOf(p.linked_expense_ids, amts).toFixed(2) }));
    }
  };

  const [untieConfirmOpen, setUntieConfirmOpen] = useState(false);
  const [nonQualifiedConfirmOpen, setNonQualifiedConfirmOpen] = useState(false);
  const [untieCount, setUntieCount] = useState(0);
  const pendingSaveRef = React.useRef(null);

  const doSave = async () => {
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    if (!f.fund_account_id) { toast.error("Select a 529 account"); return; }
    if (!f.amount) { toast.error("Enter amount"); return; }
    // Save works only once the tax and penalty box is ticked (ZEN-842).
    if (!tickCovers(allowanceRisk, allowanceTick)) { toast.error("Tick the box under the red note to save."); return; }
    if (f.date_received && f.date_requested && f.date_received < f.date_requested) {
      toast.error("Date received cannot be before date requested"); return;
    }
    // Non-qualified confirmation gate
    if (f.distribution_type === "non_qualified") {
      setNonQualifiedConfirmOpen(true);
      return;
    }
    // Untie safety net: if editing and some previously-tied expenses are now unchecked
    if (editingWithdrawal && initialTiedIds.current.length > 0) {
      const removed = initialTiedIds.current.filter((id) => !f.linked_expense_ids.includes(id));
      if (removed.length > 0) {
        setUntieCount(removed.length);
        setUntieConfirmOpen(true);
        return; // Wait for confirm
      }
    }
    await executeSave();
  };

  const continueAfterNonQualifiedConfirm = async () => {
    setNonQualifiedConfirmOpen(false);
    // Still check untie gate
    if (editingWithdrawal && initialTiedIds.current.length > 0) {
      const removed = initialTiedIds.current.filter((id) => !f.linked_expense_ids.includes(id));
      if (removed.length > 0) {
        setUntieCount(removed.length);
        setUntieConfirmOpen(true);
        return;
      }
    }
    await executeSave();
  };

  const executeSave = async () => {
    // A TICKED CROSS-YEAR COST WITHOUT ITS NOTE NEVER LEAVES THE BROWSER. The
    // server refuses it too — that is the rule, and this is only the screen
    // being honest about it before the whole save fails on one row.
    // ...AND ONLY WHEN THOSE COSTS ARE ACTUALLY BEING SAVED. "Select expenses
    // later", or switching to a kind of distribution that has no costs behind
    // it, discards every match in the payload — so demanding a note for a cost
    // this save is about to throw away blocks her on a field she may not even be
    // able to see any more. (Diff review, round 23.)
    if (!skipExpenses && f.distribution_type === "qualified_education"
        && crossYearMissingNote.length > 0) {
      toast.error("Add a note to every cost you ticked from a different calendar year.");
      return;
    }
    setSaving(true);
    const isEdit = !!editingWithdrawal;
    const isQualified = f.distribution_type === "qualified_education";
    const body = {
      // WHICH VERSION OF THE RECORD THIS SCREEN WAS SHOWING. (ZEN-182)
      //
      // Saving REPLACES the whole distribution, so a save built from a screen
      // somebody else has already changed writes stale values back over their
      // work — the amount, the 529 account, the dates — with a green tick and no
      // warning. Sending the version this form was built from is what lets the
      // server refuse that instead of silently doing it.
      //
      // ONLY ON AN EDIT, and undefined when the record predates the field, which
      // JSON simply omits. A save that names no version is accepted exactly as
      // it is today, so a tab that was already open when this shipped keeps
      // working. The server treats an absent version as "cannot tell" rather
      // than as "current" — see _stale_edit_detail in the withdrawals route.
      ...(isEdit && recordVersion != null ? { record_version: recordVersion } : {}),
      student_id: studentId, fund_account_id: f.fund_account_id,
      distribution_type: f.distribution_type,
      mode: isQualified ? f.mode : "reimbursement",
      expense_group: isQualified ? f.expense_group : "post_secondary",
      date_requested: f.date_requested,
      amount: parseFloat(f.amount), intended_recipient: f.intended_recipient, actual_recipient: f.actual_recipient,
      actual_recipient_party_id: f.actual_recipient === "party" ? f.actual_recipient_party_id : null,
      actual_recipient_other: f.actual_recipient === "other" ? f.actual_recipient_other : "",
      actual_recipient_confirmed: f.actual_recipient_confirmed, actual_recipient_note: f.actual_recipient_note,
      balance_prior: f.balance_prior ? parseFloat(f.balance_prior) : null, balance_prior_date: f.balance_prior_date,
      date_received: f.date_received || null, notes: f.notes,
      ...matchFields(),
      // THE TICK BOX, as the figure it was ticked for (ZEN-842). Omitted when
      // nothing is past the allowance.
      ...(ackToSend(allowanceRisk, allowanceTick) != null
        ? { allowance_ack_usd: ackToSend(allowanceRisk, allowanceTick) } : {}),
      loan_servicer: f.distribution_type === "student_loan_repayment" ? f.loan_servicer : "",
      roth_account_note: f.distribution_type === "roth_rollover" ? f.roth_account_note : "",
      trustee_to_trustee: f.distribution_type === "roth_rollover" ? f.trustee_to_trustee : true,
    };
    try {
      const { data: result } = isEdit
        ? await api.put(`/withdrawals/${editingWithdrawal.id}`, body)
        : await api.post("/withdrawals", body, withSaveKey(saveKey));
      // A FILE THAT DID NOT UPLOAD MUST NOT END ON A GREEN "SAVED". (ZEN-446)
      //
      // This loop used to catch each failure, print a red message, carry on,
      // and fall through to "Distribution recorded" — and the green one, being
      // last, is the one a person believes. The distribution is already
      // written by the time these run, so the files that DID attach are kept
      // and the failure simply gets the last word instead of being buried.
      let docMiss = null;
      if (docFiles.length > 0 && result?.id) {
        const { ok, failed } = await runUploads(
          docFiles,
          async (df) => {
            const fd = new FormData();
            fd.append("file", df);
            await api.post(`/withdrawals/${result.id}/document`, fd);
          },
          apiErr,
        );
        docMiss = attachmentFailureMessage({
          noun: "529 distribution",
          total: docFiles.length,
          attached: ok.length,
          failed,
          // Unlike a gift, a saved distribution CAN be re-opened and given its
          // paperwork later — the edit box lists what is attached and takes
          // more — so this one closes and points there rather than holding the
          // box open.
          retry: failed.length === 1
            ? "Open the distribution to attach it again."
            : "Open the distribution to attach them again.",
        });
      }
      // A SAVE THAT MATCHED FEWER COSTS THAN SHE PICKED IS NOT A SUCCESS, and
      // this door has the same silence the tie picker had. The withdrawal itself
      // saved, so the dialog closes and the record is refreshed — but she is told
      // in the server's own words which costs did not go with it, instead of a
      // green tick over a partial result she would only find by counting.
      // (ZEN-273.)
      const _labelPending = (result && result.label_pending_expense_ids) || [];
      if (_labelPending.length > 0) {
        toast.warning(result.label_pending_note ||
          "The match was recorded, but the cost still counts toward the year it was paid. Open the expense to move its year.");
      }
      const _unmatched = (result && result.unmatched_expense_ids) || [];
      if (_unmatched.length > 0) {
        toast.error(result.unmatched_note || "Some costs could not be matched.");
      } else if (!docMiss) {
        toast.success(isEdit ? "Distribution updated" : "Distribution recorded");
      }
      // LAST, deliberately. A missing document is the thing most easily lost
      // for good, so it is the message left on screen — never one a success
      // note is stacked on top of. (ZEN-446)
      if (docMiss) toast.error(docMiss.title, { description: docMiss.description });
      onSaved(); onClose();
    } catch (e) {
      // Held for the tick: show the red note and tick box from the refusal,
      // so the save can be ticked and sent again (ZEN-842).
      if (noteAllowanceRefusal(e)) { setAllowanceTick(null); toast.error("Tick the box under the red note to save."); return; }
      toast.error(apiErr(e));
    }
    finally { setSaving(false); }
  };
  const save = doSave;
  const exception = !["student", "institution"].includes(f.actual_recipient);

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto">
        <DialogHeader><DialogTitle>529 distribution</DialogTitle><DialogDescription className="sr-only">Record a 529 distribution</DialogDescription></DialogHeader>
        {figureChanged && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="wd-figure-changed">
            {FIGURE_CHANGED_NOTE}
          </div>
        )}
        {!editingWithdrawal && <UntiedWithdrawalReminder withdrawals={untied} onTie={onTieUntied} testid="wd-untied-reminder"
          plan={fromFigure?.plan ? { ids: fromFigure.plan.ids || [], sentence: formPlanSentence(fromFigure.plan, money) } : null} />}
        {/* Distribution type selector */}
        <div>
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Distribution type</span>
          <div className="flex flex-wrap gap-1.5">
            {DIST_TYPES.map((d) => (
              <button key={d.key} type="button" onClick={() => set("distribution_type", d.key)} data-testid={`wd-dist-${d.key}`}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${f.distribution_type === d.key ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
        {/* 529 Expense Group Toggle — only for qualified_education */}
        {f.distribution_type === "qualified_education" && (
          <div>
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Expense group</span>
            <div className="flex flex-wrap gap-1.5">
              {EXPENSE_GROUPS.map((g) => (
                <button key={g.key} type="button" onClick={() => set("expense_group", g.key)} data-testid={`wd-group-${g.key}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${f.expense_group === g.key ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white text-[#374151] hover:bg-[#E9E9EB]"}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {f.distribution_type === "qualified_education" && (
            <F label="Mode"><select className="inp" value={f.mode} onChange={(e) => set("mode", e.target.value)} data-testid="wd-mode"><option value="reimbursement">Reimbursement (expense-matched)</option><option value="lump_sum">Lump sum (front-loaded)</option></select></F>
          )}
          <F label="529 account"><select className="inp" value={f.fund_account_id} onChange={(e) => set("fund_account_id", e.target.value)} data-testid="wd-fund">{fundChoices.map((x) => <option key={x.id} value={x.id}>{fundOptionLabel(x)}{x.archived ? " (archived)" : ""}</option>)}</select></F>
          <F label="Date requested"><UnifiedDateInput value={f.date_requested} onChange={(v) => set("date_requested", v)} testid="wd-date" onValidation={onDateValidation("date_requested")} /></F>
          <F label="Amount"><MoneyInput currency="USD" value={f.amount} onChange={(v) => autoFit(v)} testid="wd-amount" /></F>
          {/* NO DROPDOWN. The year a distribution is filed under is the year the
              money actually left the plan, and picking it by hand let a record be
              FILED under one year while the rule deciding which costs it may pay
              for used another. Same value now, from the same dates. (ZEN-172) */}
          <F label="Counts toward tax year"><div className="inp flex items-center" data-testid="wd-taxyear">
            {derivedTaxYear ?? "—"}
            <span className="ml-2 text-[11px] text-[#9aa1a9]">
              {derivedTaxYear ? "set by the dates above" : "enter the dates above"}
            </span>
          </div></F>
          <F label={<span className="flex items-center gap-1">Date received <InfoTip text="Enter the date the funds actually arrived in the beneficiary's account. This is what triggers 'Reconciled' status on the Overview and should match the 1099-Q." testid="wd-received-info" /></span>}>
            <UnifiedDateInput value={f.date_received} onChange={(v) => set("date_received", v)} testid="wd-received" onValidation={onDateValidation("date_received")} className={!f.date_received ? "!border-[#D97706] !bg-[#D97706]/5" : f.date_received && f.date_requested && f.date_received < f.date_requested ? "!border-[#F01919] !bg-[#F01919]/5" : ""} />
            {f.date_received && f.date_requested && f.date_received < f.date_requested && (
              <div className="mt-1 text-[11px] font-semibold text-[#F01919]" data-testid="wd-received-error">Date received cannot be before date requested ({fmtDate(f.date_requested)})</div>
            )}
          </F>
          <F label="Intended recipient"><select className="inp" value={f.intended_recipient} onChange={(e) => set("intended_recipient", e.target.value)} data-testid="wd-intended"><option value="student">Beneficiary account</option><option value="institution">Institution (direct)</option></select></F>
          <F label="Actual recipient"><select className="inp" value={f.actual_recipient} onChange={(e) => set("actual_recipient", e.target.value)} data-testid="wd-actual"><option value="student">Beneficiary</option><option value="institution">Institution</option><option value="owner">{parties?.owner?.nickname || "Owner"}</option>{(parties?.custom || []).map((p) => <option key={p.id} value="party" data-pid={p.id}>{p.nickname}</option>)}<option value="other">Other (free text)</option></select></F>
          {f.actual_recipient === "party" && (
            <F label="Which party"><select className="inp" value={f.actual_recipient_party_id} onChange={(e) => set("actual_recipient_party_id", e.target.value)} data-testid="wd-actual-party"><option value="">Select…</option>{(parties?.custom || []).map((p) => <option key={p.id} value={p.id}>{p.nickname}</option>)}</select></F>
          )}
          {f.actual_recipient === "other" && (
            <F label="Actual recipient (other)"><input className="inp" value={f.actual_recipient_other} onChange={(e) => set("actual_recipient_other", e.target.value)} data-testid="wd-actual-other" placeholder="Describe who received it" /></F>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <F label="529 balance before this distribution" info={BALANCE_ENTRY_TIP}><MoneyInput currency="USD" value={f.balance_prior} onChange={(v) => set("balance_prior", v)} testid="wd-balance" placeholder="optional" /></F>
          {/* Show the date only alongside a balance. A stored date with no
              balance stays in the record (it may be all that survives of one)
              but must not be presented as though it were entered — the input
              falls back to its Settings-format placeholder. */}
          <F label="Date of 529 balance"><UnifiedDateInput value={f.balance_prior ? f.balance_prior_date : ""} onChange={(v) => set("balance_prior_date", v)} testid="wd-balance-date" disabled={!f.balance_prior} onValidation={onDateValidation("balance_date")} /></F>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <DecemberWithdrawalNote dateRequested={f.date_requested} />
          <OwnerRecipientNote actualRecipient={f.actual_recipient} />
        </div>

        {/* Type-specific fields: Student Loan Repayment */}
        {f.distribution_type === "student_loan_repayment" && (
          <div className="rounded-lg border border-[#0F766E]/20 bg-[#0F766E]/5 p-3" data-testid="wd-loan-fields">
            <span className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-[#0F766E]">Student loan details</span>
            <F label="Loan servicer (optional)"><input className="inp" value={f.loan_servicer} onChange={(e) => set("loan_servicer", e.target.value)} data-testid="wd-loan-servicer" placeholder="e.g., Navient, FedLoan, SoFi" /></F>
          </div>
        )}

        {/* Type-specific fields: Roth IRA Rollover */}
        {f.distribution_type === "roth_rollover" && (
          <div className="rounded-lg border border-[#0369A1]/20 bg-[#0369A1]/5 p-3 space-y-3" data-testid="wd-roth-fields">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-[#0369A1]">Roth IRA rollover details</span>
            <label className="flex items-start gap-2 text-sm" data-testid="wd-roth-t2t-label">
              <input type="checkbox" checked={f.trustee_to_trustee} onChange={(e) => set("trustee_to_trustee", e.target.checked)} data-testid="wd-roth-t2t" className="mt-0.5" />
              <span>
                Direct trustee-to-trustee transfer (the 529 sends the money straight to the Roth IRA — you never receive it)
                <InfoTip testid="wd-roth-t2t-info" width="w-80">Only a direct trustee-to-trustee transfer from the 529 to the beneficiary&apos;s Roth IRA is a qualified rollover. The 529 sends the money straight to the Roth IRA — you never receive it. If the owner receives the cash and redeposits it, the IRS treats it as a non-qualified distribution &mdash; the earnings are taxable and subject to the 10% penalty.</InfoTip>
              </span>
            </label>
            {!f.trustee_to_trustee && (
              <div className="flex items-start gap-2 rounded-md border border-[#F01919]/30 bg-[#FFF5F5] px-3 py-2 text-xs text-[#991B1B]" data-testid="wd-roth-nq-warning">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                <span>Not a qualified rollover. The earnings portion of this distribution is taxable and subject to the 10% penalty. This will be recorded as a non-qualified distribution.</span>
              </div>
            )}
            <F label="Roth account note (optional)"><input className="inp" value={f.roth_account_note} onChange={(e) => set("roth_account_note", e.target.value)} data-testid="wd-roth-note" placeholder="e.g., Fidelity Roth IRA ending 4321" /></F>
          </div>
        )}

        {/* Non-qualified warning */}
        {f.distribution_type === "non_qualified" && (() => {
          const selFund = funds.find((fa) => fa.id === f.fund_account_id);
          const isRecapture = selFund?.recapture_state;
          const fundStateFull = selFund?.state ? stateName(selFund.state) : null;
          const ownerTaxState = tenantSettings?.tax_residency_state || "";
          // Show recapture warning when: plan state is recapture AND (owner hasn't set tax state → hedge, OR owner's tax state matches plan state)
          const showRecapture = isRecapture && fundStateFull && (!ownerTaxState || ownerTaxState === selFund?.state);
          return (
            <div className="space-y-2" data-testid="wd-nq-warning">
              <div className="flex items-start gap-2 rounded-md border border-[#F01919]/30 bg-[#FFF5F5] px-3 py-2 text-xs text-[#991B1B]">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                <span>Non-qualified distribution: the earnings portion is subject to federal income tax plus a 10% additional tax penalty. State income tax may also apply.</span>
              </div>
              {showRecapture && (
                <div className="flex items-start gap-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="wd-recapture-warning">
                  <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                  <span>
                    Because this plan is in {fundStateFull}, if you claimed {fundStateFull}&apos;s state tax deduction on your contributions, this non-qualified withdrawal may require paying part of it back (recapture).
                    <InfoTip testid="wd-recapture-info" width="w-80">Many states give a state income-tax deduction for contributions to their own 529. If you claimed that deduction and later take a non-qualified withdrawal, the state can &quot;recapture&quot; it — add it back to your state taxable income. It only applies if you actually claimed the deduction, only in that state, and generally only while you still file taxes there. So it may not apply if you bought the plan while living elsewhere, never claimed the deduction, or have since moved away. Your situation depends on that state&apos;s rules — confirm with your tax advisor. Guidance only, not tax advice.</InfoTip>
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Statutory cap usage warnings for capped distribution types */}
        {(() => {
          const capTypeMap = {
            roth_rollover: ["roth_ira", "roth_annual"],
            student_loan_repayment: ["student_loan"],
          };
          const relevantTypes = capTypeMap[f.distribution_type];
          if (!relevantTypes || !capData?.statutory_caps) return null;
          const caps = capData.statutory_caps.filter((c) => relevantTypes.includes(c.type));
          if (!caps.length) return null;
          const amt = parseFloat(f.amount) || 0;
          // In edit mode, subtract the stored amount so we don't double-count
          const editingStoredAmt = editingWithdrawal?.distribution_type === f.distribution_type
            ? (parseFloat(editingWithdrawal.amount) || 0) : 0;
          return caps.map((c) => {
            const effectiveClaimed = Math.max(0, c.claimed - editingStoredAmt);
            const effectiveRemaining = round2(c.cap - effectiveClaimed);
            const isOver = effectiveClaimed > c.cap + 0.005;
            const wouldExceed = amt > 0 && (effectiveClaimed + amt) > c.cap + 0.005;
            const borderClr = isOver ? "border-[#DC2626]" : wouldExceed ? "border-[#D97706]" : "border-[#C2C9D1]";
            const bgClr = isOver ? "bg-[#DC2626]/5" : wouldExceed ? "bg-[#D97706]/5" : "bg-[#F4F7FB]";
            return (
              <div key={c.type} className={`rounded-md border ${borderClr} ${bgClr} p-2.5 text-xs`} data-testid={`wd-cap-${c.type}`}>
                <div className="font-semibold text-[#374151]">{c.label} — {c.scope === "lifetime" ? "lifetime" : "annual"} cap</div>
                <div className="mt-0.5 text-[#6b7280]">
                  Cap {money(c.cap, "USD")} · {money(effectiveClaimed, "USD")} used · <span className={effectiveRemaining < 0 ? "text-[#DC2626]" : "text-[#22C55E]"}>{money(effectiveRemaining, "USD")} remaining</span>
                </div>
                {amt > 0 && <div className="mt-0.5 text-[#6b7280]">This distribution: {money(amt, "USD")} → new total {money(effectiveClaimed + amt, "USD")}</div>}
                {isOver && <div className="mt-1 font-semibold text-[#DC2626]">Already over the cap by {money(Math.abs(effectiveRemaining), "USD")}</div>}
                {!isOver && wouldExceed && <div className="mt-1 font-semibold text-[#D97706]">This distribution would exceed the cap by {money(effectiveClaimed + amt - c.cap, "USD")}</div>}
                {c.unverified && <div className="mt-1 text-[10px] text-[#D97706]">This limit hasn&apos;t been confirmed for {derivedTaxYear} — verify with IRS guidance.</div>}
                <div className="mt-1 text-[10px] text-[#9CA3AF]">{c.description}</div>
              </div>
            );
          });
        })()}

        <PastAllowanceTick risk={allowanceRisk} tickedFor={allowanceTick} onTick={setAllowanceTick} />

        {/* Safe to withdraw info — only for qualified_education */}
        {f.distribution_type === "qualified_education" && capData?.aqee && (() => {
          const rawStw = capData.aqee.safe_to_withdraw ?? 0;
          // In edit mode, add back the editing withdrawal's stored amount (it's already counted in total_withdrawn)
          const editingStoredAmt = editingWithdrawal ? (parseFloat(editingWithdrawal.amount) || 0) : 0;
          const effectiveLimit = rawStw + editingStoredAmt;
          const stw = editingWithdrawal ? effectiveLimit : rawStw;
          // Ahead of costs or beyond them: the one shared split, with this
          // withdrawal's own amount set aside first (ZEN-806).
          const { over: overBy, awaiting: awaitingBy, ahead: aheadBy, beyond: beyondBy } = overWithdrawnSplit(capData.aqee, editingStoredAmt);
          // Money covering paid costs waiting for a final exchange rate, in the
          // Overview card's words (ZEN-835, Maryse 2 Nov 2026).
          const awaitingLine = awaitingBy > 0.005 && (
            <div className="mt-1 text-[#92400E]" data-testid="wd-stw-awaiting-part">
              <b className="tnum">{money(awaitingBy, "USD")}</b> {headlineWords("awaiting", derivedTaxYear)}. {AWAITING_RATE_SENTENCE}
            </div>
          );
          const amt = parseFloat(f.amount) || 0;
          const excess = amt > 0 ? Math.max(0, amt - stw) : 0;
          const isOverWithdrawn = overBy > 0.005;
          return (
            <div className="mt-3" data-testid="wd-safe-to-withdraw">
              {!isOverWithdrawn && stw > 0.005 && (
                <div className="rounded-md border border-[#077A39]/20 bg-[#F0FFF4] px-3 py-2 text-xs text-[#065F28]" data-testid="wd-stw-info">
                  You can withdraw up to <b className="tnum">{money(stw, "USD")}</b> for {derivedTaxYear} with no taxable earnings.
                </div>
              )}
              {isOverWithdrawn && beyondBy > 0.005 && (
                <div className="rounded-md border border-[#F01919]/30 bg-[#FFF5F5] px-3 py-2 text-xs text-[#991B1B]" data-testid="wd-stw-overdrawn">
                  Already <b className="tnum">{money(beyondBy, "USD")}</b> beyond {derivedTaxYear}&rsquo;s qualified costs. Additional withdrawals will increase the taxable amount.
                  {aheadBy > 0.005 && (
                    <div className="mt-1 text-[#92400E]" data-testid="wd-stw-ahead-part">
                      Costs still to come in {derivedTaxYear} cover the other <b className="tnum">{money(aheadBy, "USD")}</b>.
                    </div>
                  )}
                  {awaitingLine}
                </div>
              )}
              {isOverWithdrawn && !(beyondBy > 0.005) && (
                <div className="rounded-md border border-[#D97706]/40 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="wd-stw-ahead">
                  {aheadBy > 0.005 && (
                    <>Already <b className="tnum">{money(aheadBy, "USD")}</b> withdrawn ahead of {derivedTaxYear}&rsquo;s costs. Covered by costs still to come in {derivedTaxYear}. Nothing to do, as long as they&apos;re paid by December 31. Withdrawing more than those costs can cover would be taxable.</>
                  )}
                  {awaitingLine}
                </div>
              )}
              {excess > 0.005 && !isOverWithdrawn && (
                <div className="mt-1.5 flex items-start gap-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-[11px] leading-relaxed text-[#92400E]" data-testid="wd-stw-excess-warning">
                  <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-[#D97706]" />
                  <span>
                    This 529 distribution exceeds your remaining qualified expenses by <b className="tnum">{money(excess, "USD")}</b> — the earnings portion of the excess may be taxable.
                    See the estimate on <button onClick={() => { window.open("/scholarships", "_blank"); }} className="font-semibold underline">Scholarships &amp; Aid</button>.
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Expense picker — only for qualified_education */}
        {f.distribution_type === "qualified_education" && (
          <>
            <label className="mt-3 flex items-center gap-2 text-sm" data-testid="wd-skip-expenses">
              <input type="checkbox" checked={skipExpenses} onChange={(e) => setSkipExpenses(e.target.checked)} />
              <span>Select expenses later — I'll tie expenses when I'm ready</span>
            </label>
            {!skipExpenses && (filteredPreviewItems.length > 0 || priorYearItems.length > 0
              || unmatchableItems.length > 0 || rateWaitItems.length > 0 || serverPredatesTheFlag) && (
              <div className="mt-3 rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] p-3" data-testid="wd-eligible-picker">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Eligible to withdraw · {taxYearStr}</span>
                  <span className="tnum whitespace-nowrap text-sm font-extrabold text-[#077A39]" data-testid="wd-eligible-total">{money(selTotal, "USD")} · {f.linked_expense_ids.length} selected</span>
                </div>
                {/* "or due to be paid" is not padding: a cost still ahead of its
                    date but in the same calendar year is legitimately offered
                    here (ZEN-158's pre-funding), and "costs paid in {year}"
                    would state something untrue about every one of them. */}
                <p className="mb-2 text-[11px] text-[#6b7280]">Showing costs paid — or due to be paid — in <b>{taxYearStr}</b>, the calendar year this withdrawal falls in. Type the <b>Amount</b> to auto-fill oldest-first, or tick rows manually.</p>
                {serverPredatesTheFlag && (
                  <p className="mb-2 rounded border border-[#D97706]/30 bg-[#FFFBEB] px-2 py-1 text-[11px] text-[#92400E]" data-testid="wd-picker-stale-page">
                    This page is newer than the server it is talking to. Reload in a minute.
                  </p>
                )}
                {unmatchableItems.length > 0 && filteredPreviewItems.length === 0 && (
                  <p className="text-[11px] text-[#6b7280]" data-testid="wd-picker-no-readable-date">
                    This withdrawal&apos;s date could not be read, so no costs can be matched to it. Correct the date first.
                  </p>
                )}
                <div className="max-h-44 space-y-0.5 overflow-y-auto">
                  {filteredPreviewItems.map((it) => {
                    const checked = f.linked_expense_ids.includes(it.id);
                    const amt = allocAmts[it.id];
                    // ═══ WHAT IS LEFT ON THIS ROW IS WHAT MAY GO ONTO IT ═══
                    //
                    // These two lines used to measure against `withdraw_usd`,
                    // which is what the EXPENSE comes to — not what this
                    // distribution may put on it. On a cost carried over from
                    // another calendar year those are wildly different: it may
                    // keep the amount already matched to it and not a penny
                    // more, so a US$2,000 expense holding US$500 was drawn with an
                    // editable box and a badge reading "US$1,500.00 left".
                    //
                    // Every one of those words was false. She cannot move that
                    // money; typing into the box was silently clamped back; and
                    // the figure sat on the screen as an invitation. That is
                    // ZEN-190's remaining half, and it is the same disagreement
                    // the Overview had — one rule now answers both. (ZEN-190)
                    const cap = ceilingFor(it);
                    const fixedAtWhatItHolds = isFixedAtWhatItHolds(it);
                    const isPartial = checked && amt != null && amt !== "" && Number(amt) + 0.005 < cap;
                    const balance = isPartial ? round2(cap - Number(amt)) : 0;
                    const foreign = !isUSD(it.original_currency) ? <span className="ml-1 text-[10px] text-[#9aa1a9]">({money(it.original_amount, it.original_currency)})</span> : null;
                    const prepaid = it.eligibility_tax_year && String(it.eligibility_tax_year) !== (it.transaction_date || "").slice(0, 4);
                    return (
                    <React.Fragment key={it.id}>
                    <div className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 rounded px-1.5 py-1 text-xs hover:bg-white ${it.is_future ? "opacity-80" : ""}`} data-testid={`wd-elig-${it.id}`}>
                      {/* NOTHING ON A COST ROW IS EVER HIDDEN. When the row is
                          too narrow for everything on one line, the pieces
                          move down a line: the payee wraps instead of being
                          cut short, and the amount drops beneath it, still at
                          the right-hand edge. Shortening the payee to fit is
                          how its name vanished completely on a phone. (ZEN-303) */}
                      <label className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                        <input type="checkbox" checked={checked} onChange={() => toggleExp(it.id)} data-testid={`wd-elig-check-${it.id}`} />
                        <span className="tnum">{fmtDate(it.transaction_date)}</span>
                        {prepaid && <span className="rounded-full bg-[#7D3306]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#7D3306]">prepaid</span>}
                        {it.is_future && <span className="rounded-full bg-[#E9E9EB] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#6b7280]">future</span>}
                        <span className="min-w-0 break-words font-semibold" data-testid={`wd-elig-payee-${it.id}`}>{it.payee}</span>
                        {it.already_allocated > 0.005 && <span className="whitespace-nowrap rounded-full bg-[#7D3306]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#7D3306]">{money(it.already_allocated, "USD")} of {money(it.full_usd, "USD")} done</span>}
                        {setAsideNote(it.set_aside_usd) && <span className="whitespace-nowrap text-[10px] text-[#6b7280]" data-testid={`wd-elig-setaside-${it.id}`}>{setAsideNote(it.set_aside_usd)}</span>}
                      </label>
                      {/* RENT THE ALLOWANCE HAS NO ROOM FOR (ZEN-842, item 6):
                          the way to take it off the 529 is to change who pays it
                          back, on the expense itself. */}
                      {it.allowance_school && (allowanceRoomNow[it.allowance_school] ?? 1) <= 0.005 && (!checked || isPartial) && (
                        <Link to={`/expenses?open=${it.id}`} className="basis-full text-[10px] font-semibold text-[#034AA2] hover:underline" data-testid={`wd-elig-pay-outside-${it.id}`}>
                          {PAY_REST_OUTSIDE_LINK} →
                        </Link>
                      )}
                      <div className="ml-auto flex shrink-0 items-center gap-1.5">
                        {it.fx_basis === "withdrawal_date" && <span className="text-[9px] text-[#D97706]" title="FX rate at withdrawal date">FX@wd</span>}
                        {it.fx_basis === "expense_date" && !isUSD(it.original_currency) && <span className="text-[9px] text-[#034AA2]" title="FX rate at paid date">FX@paid</span>}
                        {!checked ? (
                          <span className="tnum font-semibold text-[#9aa1a9]" data-testid={`wd-elig-amt-${it.id}`}>{money(it.withdraw_usd, "USD")}{foreign}</span>
                        ) : isPartial ? (
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <MoneyInput currency="USD" size="sm" warning className="!w-32" value={amt ?? ""} onChange={(v) => setRowAmt(it.id, v)} testid={`wd-elig-amt-${it.id}`} placeholder="" />
                            <span className="rounded-full bg-[#7D3306]/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#7D3306]">{money(balance, "USD")} left</span>
                          </span>
                        ) : (
                          <span className="tnum font-semibold" data-testid={`wd-elig-amt-${it.id}`}>{money(Number(amt), "USD")}{foreign}</span>
                        )}
                      </div>
                    </div>
                    {/* SAY WHY THE FIGURE CANNOT MOVE, rather than just refusing
                        to let it. A row fixed at what it already holds now looks
                        exactly like a row she simply has not changed, and the
                        only difference she would ever notice is that typing into
                        it does nothing. Silently limiting a money field is how
                        somebody concludes the app is broken and works around it.
                        (ZEN-190) */}
                    {checked && fixedAtWhatItHolds && (
                      <div className="mb-1 px-1.5 text-[10px] leading-relaxed text-[#92400E]"
                           data-testid={`wd-elig-fixed-${it.id}`}>
                        <b>Fixed at {money(ceilingFor(it), "USD")}.</b>{" "}
                        {/* THE SERVER'S OWN SENTENCE, NOT ONE WRITTEN HERE.
                            The first draft of this line said the cost was paid
                            in another calendar year — which is only one of the
                            two reasons a row can be fixed, and simply wrong on a
                            expense whose whole value is already covered. The screen
                            cannot tell those apart; the server can, and now
                            sends the words. Where it does not (a page newer than
                            the server), this says the part that is true in every
                            case and claims no cause at all. */}
                        {it.may_increase_note
                          || "This amount can be kept as it is, but not increased."}
                      </div>
                    )}
                    {checked && isGrowing(it) && (
                      <label className="mb-1 block px-1.5" data-testid={`wd-elig-note-wrap-${it.id}`}>
                        <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#B45309]">
                          Paid in {it.paid_year} &mdash; why do these belong together?
                        </span>
                        <textarea rows={2} maxLength={2000} value={(crossYearNotes[it.id] || {}).note || ""}
                          onChange={(ev) => setCrossYearNotes((p) => ({ ...p, [it.id]: { note: ev.target.value, facts: noteFacts(it, cyDist) } }))}
                          placeholder="For example: paid the university early for next year's study."
                          className="w-full rounded border border-[#C2C9D1] px-2 py-1 text-xs"
                          data-testid={`wd-elig-note-${it.id}`} />
                      </label>
                    )}
                    </React.Fragment>
                  );})}
                </div>
                {/* COSTS PAID IN AN EARLIER CALENDAR YEAR — listed, explained,
                    and TICKABLE with a note. Grouped by the year they were PAID,
                    most recent first. Only the EARLIER direction reaches this
                    block since ZEN-331: a cost paid AFTER the money came out is
                    refused entirely and renders in its own bucket below with no
                    tick-box.

                    THE WORDS CHANGED WITH THE RULE. This block used to say these
                    costs "cannot be tied to this one", which was true when
                    ZEN-173 shipped the refusal without the door on the other
                    side. It is not true now, and a screen that keeps saying it
                    would send the account owner off to falsify a payment date or
                    delete a real tie — the two remedies this ticket exists to
                    stop offering. (ZEN-174) */}
                {priorYearItems.length > 0 && (
                  <div className="mt-2 border-t border-[#E9E9EB] pt-2" data-testid="wd-different-year-group">
                    {[...new Set(priorYearItems.map((it) => it.paid_year))]
                      .sort((a, b) => b - a)
                      .map((yr) => (
                        <div key={yr} className="mb-1.5">
                          <div className="text-[11px] font-bold text-[#B45309]" data-testid={`wd-different-year-heading-${yr}`}>
                            Costs paid in {yr} — a different calendar year from this withdrawal
                          </div>
                          <p className="mb-1 text-[10px] text-[#6b7280]">
                            Best practice is to take a withdrawal in the same calendar year the cost was paid. There is no deadline in the tax code, so you can still tie these &mdash; tick one and say why. If you do, the cost will count toward this withdrawal&rsquo;s tax year on your year totals and tax documents. Your note stays in this app and never appears in your tax packet.
                          </p>
                          {priorYearItems.filter((it) => it.paid_year === yr).map((it) => {
                            const ticked = f.linked_expense_ids.includes(it.id);
                            return (
                              <div key={it.id} className={`rounded px-1.5 py-1 text-xs ${ticked ? "bg-[#FCF5EE]" : ""}`} data-testid={`wd-different-year-${it.id}`}>
                                <label className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <input type="checkbox" checked={ticked}
                                      onChange={() => toggleExp(it.id)}
                                      data-testid={`wd-different-year-check-${it.id}`} />
                                    <span className="tnum">{fmtDate(it.transaction_date)}</span>
                                    <span className="min-w-0 break-words font-semibold">{it.payee}</span>
                                  </span>
                                  <span className="tnum ml-auto shrink-0">{money(it.withdraw_usd, "USD")}</span>
                                </label>
                                {ticked && (
                                  <label className="mt-1 block">
                                    <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-wide text-[#6b7280]">Why do these belong together?</span>
                                    <textarea rows={2} maxLength={2000} value={(crossYearNotes[it.id] || {}).note || ""}
                                      onChange={(ev) => setCrossYearNotes((p) => ({ ...p, [it.id]: { note: ev.target.value, facts: noteFacts(it, cyDist) } }))}
                                      placeholder="For example: paid the university early for next year's study."
                                      className="w-full rounded border border-[#C2C9D1] px-2 py-1 text-xs"
                                      data-testid={`wd-different-year-note-${it.id}`} />
                                  </label>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    {crossYearMissingNote.length > 0 && (
                      <div className="mt-1 text-[11px] font-semibold text-[#B45309]" data-testid="wd-different-year-needs-note">
                        Add a note to {crossYearMissingNote.length === 1 ? "the cost you ticked above" : `all ${crossYearMissingNote.length} costs you ticked above`} before saving.
                      </div>
                    )}
                  </div>
                )}
                {laterYearItems.length > 0 && (
                  <div className="mt-2 border-t border-[#E9E9EB] pt-2" data-testid="wd-later-year-group">
                    <div className="text-[11px] font-bold text-[#B45309]" data-testid="wd-later-year-heading">
                      Costs paid after this withdrawal &mdash; a later calendar year
                    </div>
                    <p className="mb-1 text-[10px] text-[#6b7280]">
                      Money taken out in one year cannot cover a cost paid in a later year &mdash; at the time it came out, the cost did not exist yet. Take the withdrawal in the year you pay the expense.
                    </p>
                    {laterYearItems.map((it) => (
                      <div key={it.id} className="flex flex-wrap items-center justify-between gap-2 rounded px-1.5 py-1 text-xs text-[#6b7280]" data-testid={`wd-later-year-${it.id}`}>
                        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="tnum">{fmtDate(it.transaction_date)}</span>
                          <span className="min-w-0 break-words font-semibold">{it.payee}</span>
                        </span>
                        <span className="tnum ml-auto shrink-0">{money(it.withdraw_usd, "USD")}</span>
                      </div>
                    ))}
                  </div>
                )}
                <RateWaitRows items={rateWaitItems} testid="wd-rate-wait" />
              </div>
            )}
            {/* THE LAST WORD, AND ONLY WHEN THERE IS NOTHING ELSE TO SAY. This
                used to render whenever the tickable list was empty — which is
                exactly when the two messages above appear, so all three showed
                at once and one contradicted the others. Its wording also loses
                "tagged", which described the browser-side year filter ZEN-173
                removed. */}
            {!skipExpenses && filteredPreviewItems.length === 0 && priorYearItems.length === 0
              && unmatchableItems.length === 0 && rateWaitItems.length === 0 && !serverPredatesTheFlag
              && previewItems.length > 0 && (
              <div className="mt-3 rounded-lg border border-[#E9E9EB] bg-[#F8FAFC] p-3 text-xs text-[#6b7280]" data-testid="wd-no-eligible-for-year">
                No eligible costs paid in <b>{taxYearStr}</b>. Other years have eligible items.{" "}
                <button onClick={() => setSkipExpenses(true)} className="font-semibold text-[#034AA2] hover:underline">Select expenses later</button> to record this 529 distribution now.
              </div>
            )}
            {/* Awaiting estimate — informational only, not selectable */}
            {!skipExpenses && preview?.awaiting_estimate_total > 0.005 && (
              <div className="mt-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="wd-awaiting-estimate">
                ≈ {money(preview.awaiting_estimate_total, "USD")} in {preview.awaiting_estimate_count} foreign expense{preview.awaiting_estimate_count !== 1 ? "s" : ""} awaiting
                {preview.awaiting_estimate_count !== 1 ? " their" : " its"} final exchange rate — not yet available to withdraw against.
              </div>
            )}
            {!skipExpenses && preview?.unpriced_count > 0 && (
              <div className="mt-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="wd-not-shown-no-dollar-amount">
                {notShownNoDollarAmount(preview.unpriced_count)}
              </div>
            )}
            {/* HOW FAR THE EXCHANGE RATES REACH — shown here because this is
                where somebody decides how much to withdraw, and a foreign
                expense priced from the wrong day is wrong permanently.
                Only when there is something foreign in the picture: on an
                all-dollar withdrawal the rates decide nothing and the line
                would be one more thing to read past. */}
            {!skipExpenses && preview && "rates_are_level" in preview
              && previewItems.some((it) => it.original_currency && !isUSD(it.original_currency)) && (
              !preview.rates_current_through ? (
                /* THE CHECK COULD NOT RUN. Said out loud rather than left
                   blank: a check that silently disappears leaves this screen
                   looking exactly like one where the rates are fine, which is
                   the opposite of what asking for the check was for. */
                <div className="mt-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="wd-rates-unknown">
                  We could not check how current the exchange rates are just
                  now. Foreign expenses below may be priced from an older day's
                  rate, and that figure is kept once it is recorded. Reload in a
                  moment, or withdraw against US-dollar expenses only.
                </div>
              ) : preview.rates_are_level ? (
                <div className="mt-2 text-xs text-[#6b7280]" data-testid="wd-rates-current">
                  Exchange rates are current through <b>{fmtDate(preview.rates_current_through)}</b>.
                </div>
              ) : (
                <div className="mt-2 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]" data-testid="wd-rates-behind">
                  Exchange rates only reach <b>{fmtDate(preview.rates_current_through)}</b>. Anything
                  paid after that date is priced using the {fmtDate(preview.rates_current_through)} rate,
                  and the figure is kept once it is recorded. Wait for the rates to catch up before
                  withdrawing against those expenses.
                </div>
              )
            )}
          </>
        )}

        {f.actual_recipient !== f.intended_recipient && (
          <label className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#7D3306]"><input type="checkbox" checked={f.actual_recipient_confirmed} onChange={(e) => set("actual_recipient_confirmed", e.target.checked)} data-testid="wd-confirm" /> Confirm actual recipient differs from intended</label>
        )}
        {exception && (
          <div className="mt-2 flex items-start gap-2 rounded-md bg-[#F01919]/10 px-3 py-2 text-xs text-[#F01919]" data-testid="wd-exception-warning">
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" /> Funds landing with the owner — the 1099-Q will be in the owner's name/SSN. This will be flagged as an exception.
          </div>
        )}
        <F label="Notes" className="mt-2"><input className="inp" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="wd-notes" /></F>

        {/* Document upload — multi-file zone */}
        <F label="Supporting documents (optional)" className="mt-2">
          <DistributionDocuments withdrawal={editingWithdrawal} files={docFiles} onFilesChange={setDocFiles}
            recordVersion={recordVersion} onRecordVersion={setRecordVersion} onSaved={onSaved} />
        </F>

        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} disabled={saving || !tickCovers(allowanceRisk, allowanceTick)} data-testid="wd-save">{saving ? "Saving…" : "Save"}</Btn></DialogFooter>
        <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
      </DialogContent>
    </Dialog>
    <DecisionDialog
      open={untieConfirmOpen}
      onClose={() => setUntieConfirmOpen(false)}
      onConfirm={async () => { setUntieConfirmOpen(false); await executeSave(); }}
      title={`Untie ${untieCount} expense${untieCount !== 1 ? "s" : ""}?`}
      actionLabel="Untie"
      testid="wd-untie-confirm"
    >
      <p className="text-sm text-[#374151]">They&apos;ll return to Confirmed status and become available to tie to another 529 distribution. This doesn&apos;t delete anything.</p>
    </DecisionDialog>
    {/* THE HEADING A FAMILY CONFIRMS REAL MONEY UNDER. (ZEN-664.) It read
        "Record as non-qualified distribution?" — a phrase retired on
        2026-09-15 when the list label was chosen — because the four list
        labels are pinned by a test and this box was pinned by nothing, so the
        label change was forced to be deliberate and this box quietly kept the
        old words. Heading AND button are now pinned in
        backend/tests/test_screen_word_parity.py, so the next word change
        cannot leave this box behind a second time.
        THE BUTTON IS DELIBERATELY NOT THE HEADING. It was considered and left
        alone: it sits directly under the heading and inherits its context.
        Do not "fix" it for consistency. (Maryse, 2026-09-17.) */}
    <DecisionDialog
      open={nonQualifiedConfirmOpen}
      onClose={() => setNonQualifiedConfirmOpen(false)}
      onConfirm={() => { setNonQualifiedConfirmOpen(false); continueAfterNonQualifiedConfirm(); }}
      title="Record this as a non-qualified 529 distribution?"
      actionLabel="Record as non-qualified"
      testid="nq-confirm"
    >
      <div className="space-y-3 text-sm text-[#374151]">
        <p>A non-qualified distribution isn&apos;t matched to education expenses, a student-loan repayment, or a Roth IRA rollover.</p>
        <p>If you continue, the earnings portion of this distribution (not your contributions) is generally subject to income tax plus a 10% additional tax, on the recipient&apos;s return. The 10% is waived in some cases &mdash; for example, up to a tax-free scholarship, or on the beneficiary&apos;s death or disability &mdash; but income tax on the earnings still applies.</p>
        <p className="font-semibold">Before you continue, consider:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>If it paid for education, record it as a Qualified expense instead.</li>
          <li>For unused funds, a Roth IRA rollover (if your account qualifies) is tax-free.</li>
        </ul>
        <p>Continue only if you intend a non-qualified distribution.</p>
      </div>
    </DecisionDialog>
    </>
  );
}
function F({ label, children, className = "", info }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
        {label}{info && <InfoTip text={info} testid="wd-field-info" />}
      </span>
      {children}
    </label>
  );
}


function OverAllocTip() {
  return (
    <InfoTip testid="wd-overalloc-tip" size={12} width="w-72">
      This usually happens when an exchange rate was corrected. Options: take an additional withdrawal this calendar year to cover the difference, or leave it — the uncovered portion simply isn&apos;t matched to this 529 distribution.
    </InfoTip>
  );
}


// THE CARDS' WORDS (ZEN-797), all approved by Maryse: the past-year card on
// 23 Oct 2026; the blue card's tag, its text and the past-year line on 24 Oct
// 2026 (she simplified the blue text).
export const BREAKDOWN_WORDS = {
  blueTag: (y) => `tax year ${y}`,
  blueText: (y) => `Expenses dated later in ${y}. You can take the money out now so it is in place when they are due. Expenses in another currency are tied to the withdrawal once their date has passed and the official rate is in.`,
  priorTitle: (y) => `Tax year ${y}: not matched to a 529 withdrawal`,
  priorText: (y) => `These ${y} expenses are not tied to any ${y} withdrawal you recorded. If the 529 paid for them in ${y}, record or tie that withdrawal. If you paid them yourself, there is nothing more to do: a ${y} expense cannot be drawn for in a later year.`,
  priorLine: (y, amount) => `${y} also has ${amount} not matched to a 529 withdrawal.`,
  seeYear: (y) => `See ${y} →`,
};

/** The summary cards follow the tax year picked with the year buttons (ZEN-797,
 *  Maryse 23 Oct 2026): a year shows only its own cards, and the current year
 *  adds one line per past year still holding expenses not matched to a
 *  withdrawal, each a link that picks that year. "All years" shows every card. */
export function BreakdownCard({ eligSummary: all_years, year = "all", onSeeYear }) {
  const currentYear = all_years?.current_tax_year || new Date().getFullYear();
  const all = year === "all" || year == null;
  const shows = (y) => all || Number(year) === Number(y);
  // Only the year picked: from here on, eligSummary is that year's view.
  const eligSummary = all_years && {
    ...all_years,
    withdrawable_now: shows(currentYear) ? all_years.withdrawable_now : 0,
    prefundable: shows(currentYear) ? all_years.prefundable : 0,
    awaiting_by_year: (all_years.awaiting_by_year || []).filter((e) => shows(e.year)),
    future_years: (all_years.future_years || []).filter((e) => shows(e.year)),
    prior_years: (all_years.prior_years || []).filter((e) => shows(e.year)),
  };
  const priorLines = !all && Number(year) === Number(currentYear)
    ? (all_years?.prior_years || []).filter((e) => e.amount > 0.005) : [];
  const hasData = eligSummary && (
    (eligSummary.withdrawable_now || 0) > 0.005 ||
    // ...INCLUDING THE PRE-FUNDABLE FIGURE ON ITS OWN. A family whose only
    // outstanding cost is next month's rent has nothing in the other three
    // buckets, so the whole card was hidden and the one number they came for
    // with it. (Diff review, round 2.)
    (eligSummary.prefundable || 0) > 0.005 ||
    (eligSummary.awaiting_by_year?.length || 0) > 0 ||
    (eligSummary.future_years?.length || 0) > 0 ||
    (eligSummary.prior_years?.length || 0) > 0 ||
    priorLines.length > 0
  );
  if (!hasData) return null;
  return (
    <Card className="mb-4" data-testid="withdrawal-breakdown-card">
      <div className="mb-3 text-xs font-bold uppercase tracking-wide text-[#6b7280]">529-eligible · not yet withdrawn</div>

      {/* GREEN — Withdrawable now */}
      {eligSummary.withdrawable_now > 0.005 && (
        <div className="mb-3 rounded-lg border border-[#077A39]/20 bg-[#F0FFF4] p-3" data-testid="breakdown-withdrawable-now">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#065F28]">Withdrawable now</span>
              <span className="rounded-full bg-[#077A39]/15 px-2 py-0.5 text-[10px] font-semibold text-[#077A39]">tax year {currentYear}</span>
            </div>
            <span className="text-lg font-extrabold tnum text-[#077A39]" data-testid="breakdown-withdrawable-now-amount">{money(eligSummary.withdrawable_now, "USD")}</span>
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-[#6b7280]">
            Settled expenses dated in {currentYear} — withdraw any time this year.{eligSummary.awaiting_by_year?.length > 0 && " Foreign expenses whose rate hasn\u2019t locked appear below."}
          </div>
        </div>
      )}

      {/* BLUE — Expenses not yet paid, same tax year. NEUTRAL, NOT AMBER: this is
          not something needing attention, it is the ordinary way rent gets paid
          — the money has to leave the plan and travel for several days before
          the 1st. Kept out of "Withdrawable now" on purpose: that figure has
          always meant a cost already incurred, and folding an unpaid expense into
          it would overstate what has been spent. (ZEN-158) */}
      {eligSummary.prefundable > 0.005 && (
        <div className="mb-3 rounded-lg border border-[#034AA2]/20 bg-[#EFF6FF] p-3" data-testid="breakdown-prefundable">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#034AA2]">Not paid yet — you can draw ahead</span>
              <span className="rounded-full bg-[#034AA2]/15 px-2 py-0.5 text-[10px] font-semibold text-[#034AA2]" data-testid="breakdown-prefundable-tag">{BREAKDOWN_WORDS.blueTag(currentYear)}</span>
            </div>
            <span className="text-lg font-extrabold tnum text-[#034AA2]" data-testid="breakdown-prefundable-amount">{money(eligSummary.prefundable, "USD")}</span>
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-[#6b7280]" data-testid="breakdown-prefundable-text">
            {BREAKDOWN_WORDS.blueText(currentYear)}
          </div>
        </div>
      )}

      {/* AMBER — Awaiting final exchange rate */}
      {eligSummary.awaiting_by_year?.length > 0 && (
        <div className="mb-3" data-testid="breakdown-awaiting-rate">
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#92400E]">Awaiting final exchange rate — can&apos;t withdraw until the rate locks</div>
          <div className="space-y-2">
            {eligSummary.awaiting_by_year.map((entry) => (
              <div key={entry.year} className="rounded-lg border border-[#D97706]/20 bg-[#FFFBEB] p-3" data-testid={`breakdown-awaiting-${entry.year}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#92400E]">Tax year {entry.year}</span>
                    <span className="rounded-full bg-[#D97706]/15 px-2 py-0.5 text-[10px] font-semibold text-[#D97706]">
                      {entry.year === currentYear ? "this year" : `January 1, ${entry.year}`}
                    </span>
                  </div>
                  <span className="text-lg font-extrabold tnum text-[#92400E]" data-testid={`breakdown-awaiting-${entry.year}-amount`}>{money(entry.amount, "USD")}</span>
                </div>
                <div className="mt-1 text-[11px] leading-relaxed text-[#6b7280]">
                  {entry.year === currentYear
                    ? "Foreign expense, rate not locked yet. Becomes withdrawable this year once the rate locks on its transaction date."
                    : `Foreign expense, rate not locked. Withdrawable in ${entry.year} once the rate locks — pay it from your own money until then.`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AMBER — Future tax year */}
      {eligSummary.future_years?.length > 0 && (
        <div className="mb-3" data-testid="breakdown-future-years">
          {/* THE WORDS CHANGE WITH THE RULE. This heading used to read "Front
              now, reimburse later", which on a withdrawal screen — beside a
              feature that now genuinely does let money go out ahead of an expense —
              reads as permission to draw 529 money this year for next year's
              cost. That is the one thing the rule refuses outright. The family
              fronts their OWN money and the 529 draw happens in the year the
              expense is paid. (ZEN-158) */}
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#92400E]">A different tax year — pay from your own money for now</div>
          <div className="space-y-2">
            {eligSummary.future_years.map((entry) => (
              <div key={entry.year} className="rounded-lg border border-[#D97706]/20 bg-[#FFFBEB] p-3" data-testid={`breakdown-future-${entry.year}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#92400E]">Tax year {entry.year}</span>
                    <span className="rounded-full bg-[#D97706]/15 px-2 py-0.5 text-[10px] font-semibold text-[#D97706]">January 1, {entry.year}</span>
                  </div>
                  <span className="text-lg font-extrabold tnum text-[#92400E]" data-testid={`breakdown-future-${entry.year}-amount`}>{money(entry.amount, "USD")}</span>
                </div>
                <div className="mt-1 text-[11px] leading-relaxed text-[#6b7280]">
                  Settled amount. Pay out of pocket now; reimburse from the 529 on or after January 1, {entry.year}.
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AMBER — Past years. INFORMATIONAL, NO WITHDRAW SUGGESTION: a closed
          year's expense cannot be drawn for in a later year. Wording approved
          by Maryse, 23 Oct 2026 (ZEN-797). */}
      {eligSummary.prior_years?.length > 0 && (
        <div data-testid="breakdown-prior-years">
          <div className="space-y-2">
            {eligSummary.prior_years.map((entry) => (
              <div key={entry.year} className="rounded-lg border border-[#D97706]/20 bg-[#FFFBEB] p-3" data-testid={`breakdown-prior-${entry.year}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-bold text-[#92400E]" data-testid={`breakdown-prior-${entry.year}-title`}>{BREAKDOWN_WORDS.priorTitle(entry.year)}</span>
                  <span className="text-lg font-extrabold tnum text-[#92400E]" data-testid={`breakdown-prior-${entry.year}-amount`}>{money(entry.amount, "USD")}</span>
                </div>
                <div className="mt-1 text-[11px] leading-relaxed text-[#6b7280]" data-testid={`breakdown-prior-${entry.year}-text`}>
                  {BREAKDOWN_WORDS.priorText(entry.year)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {priorLines.map((entry) => (
        <div key={entry.year} className="flex flex-wrap items-center gap-x-1.5 text-xs text-[#92400E]" data-testid={`breakdown-prior-line-${entry.year}`}>
          <span>{BREAKDOWN_WORDS.priorLine(entry.year, money(entry.amount, "USD"))}</span>
          {onSeeYear && (
            <button type="button" onClick={() => onSeeYear(entry.year)} className="font-semibold underline hover:text-[#78350F]"
              data-testid={`breakdown-see-${entry.year}`}>{BREAKDOWN_WORDS.seeYear(entry.year)}</button>
          )}
        </div>
      ))}
    </Card>
  );
}
